// frontend/src/components/HospitalReportForm.js

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBlocker } from 'react-router-dom'; // ★ useBlocker をインポート
import {
  AlertTriangle,
  Info,
  Loader2,
  Trash2,
  Plus,
  Save,
  FileText,
  Copy,
  Sparkles,
  RefreshCw,
  X,
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../styles/custom-quill.css';
import '../styles/HospitalReportForm.css';

// ★ 新しいcreateOrUpdateHospitalReportをimport（後述）
import {
  getResults,
  createOrUpdateHospitalReport, // まとめた関数
  getHospitalReportPDF,
  getHospitalReportById,
  getHospitalReportByCapacityId,
  getDepartmentTermCapacities,
  getCohorts,
  getHospitalReportCopyCandidates,
  classifyHospitalReportFreeform,
  getHospitalReportAiUsageStatus,
  refineHospitalReportDraft,
} from '../services/api';

import LoadingSpinner from './LoadingSpinner';
import useSessionStorage from '../hooks/useSessionStorage';  // ★ 追加
import { prepareHospitalReportPayloadImages } from '../utils/hospitalReportImages';
import {
  BUSYNESS_LABELS,
  FREE_STUDY_TIME_LABELS,
  FREE_STUDY_TIME_OPTIONS,
  calculateBusyness,
} from '../utils/hospitalReportEvaluation';

const EMPTY_EDITOR_HTML = '<p><br></p>';
const AI_SECTION_LABELS = {
  schedule: 'スケジュール',
  training: '実習内容',
  assignment: '課題',
  facility: '設備・環境',
  other: 'その他',
  hospital_tour_text: '病院見学',
};
const AI_EVALUATION_LABELS = {
  busyness: '忙しさ',
  assignment_burden: '課題・発表の負担度',
  self_study_availability: '国試勉強・自習時間の確保',
  hands_on_opportunity: '手技・実践機会の多さ',
  lodging_convenience: '宿舎の利便性・快適さ',
  teaching_quality: '教育の質',
  question_approachability: '質問のしやすさ',
  department_atmosphere: '医局の雰囲気',
  doctor_personality: '医師の人柄',
  car_requirement: '車の必要性',
  breakfast: '朝食',
  lunch: '昼食',
  dinner: '夕食',
  hospital_tour_option: '病院見学可否',
};
const AI_BUSYNESS_INPUT_LABELS = {
  typical_start_time: '始業時刻',
  typical_end_time: '終業時刻',
  late_days_per_week: '17時以降の日数',
  free_study_time: '自習時間',
  has_night_duty: '当直・夜遅い対応',
};
const AI_TOUR_OPTION_LABELS = {
  possible: '可能',
  impossible: '不可',
  unknown: '不明',
};
const AI_MEAL_OPTION_LABELS = {
  none: 'なし',
  free: '無料',
  paid: '有料',
};
const AI_ACTION_COOLDOWN_SECONDS = 20;
const EVALUATION_FIELD_GROUPS = [
  {
    section: '実習負荷',
    items: [
      { key: 'assignmentBurden', label: '課題・発表の負担度', left: '重い', right: '軽い' },
      { key: 'selfStudyAvailability', label: '国試勉強・自習時間の確保', left: 'しにくい', right: 'しやすい' },
      { key: 'handsOnOpportunity', label: '手技・実践機会の多さ', left: '見学中心', right: '多い' },
    ],
  },
  {
    section: '教育・人',
    items: [
      { key: 'teachingQuality', label: '教育の質', left: '低い', right: '高い' },
      { key: 'questionApproachability', label: '質問のしやすさ', left: '聞きにくい', right: '聞きやすい' },
      { key: 'departmentAtmosphere', label: '医局の雰囲気', left: '悪い', right: '良い' },
      { key: 'doctorPersonality', label: '医師の人柄', left: '悪い', right: '良い' },
    ],
  },
  {
    section: '生活',
    items: [
      { key: 'lodgingConvenience', label: '宿舎の利便性・快適さ', left: '不便', right: '快適' },
      { key: 'carRequirement', label: '車の必要性', left: '不要', right: '必須' },
    ],
  },
];

function isEditorContentEmpty(content) {
  if (!content) {
    return true;
  }

  const normalized = String(content)
    .replace(/\s/g, '')
    .replace(/<p><br><\/p>/gi, '')
    .replace(/<p><\/p>/gi, '')
    .replace(/<br\/?>/gi, '');

  return normalized === '';
}

function buildDoctorState(doctor, index) {
  return {
    id: index + 1,
    doctorName: doctor?.doctor_name || doctor?.doctorName || '',
    doctorNote: doctor?.doctor_note || doctor?.doctorNote || EMPTY_EDITOR_HTML,
  };
}

function getAiErrorMessage(error, fallbackMessage) {
  return error?.response?.data?.detail || fallbackMessage;
}

function formatAiEvaluationValue(fieldName, value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (fieldName === 'hospital_tour_option') {
    return AI_TOUR_OPTION_LABELS[value] || value;
  }

  if (['breakfast', 'lunch', 'dinner'].includes(fieldName)) {
    return AI_MEAL_OPTION_LABELS[value] || value;
  }

  if (fieldName === 'busyness') {
    return `${value} (${BUSYNESS_LABELS[value] || '未分類'})`;
  }

  return String(value);
}

function formatAiBusynessInputValue(fieldName, value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (fieldName === 'late_days_per_week') {
    return `${value}日/週`;
  }

  if (fieldName === 'free_study_time') {
    return FREE_STUDY_TIME_LABELS[value] || value;
  }

  if (fieldName === 'has_night_duty') {
    return value ? 'あり' : 'なし';
  }

  return String(value);
}

function HospitalReportAiWarnings({ warnings }) {
  if (!Array.isArray(warnings) || warnings.length === 0) {
    return null;
  }

  return (
    <div className="hospital-report-ai-warnings">
      <div className="hospital-report-ai-warnings-title">注意事項</div>
      <ul className="list-disc pl-5 space-y-1">
        {warnings.map((warning, index) => (
          <li key={`${warning}-${index}`}>{warning}</li>
        ))}
      </ul>
    </div>
  );
}

function HospitalReportAiHtmlPreview({ label, value }) {
  if (value === null || value === undefined || isEditorContentEmpty(value)) {
    return null;
  }

  return (
    <div className="hospital-report-ai-preview-item">
      <div className="hospital-report-ai-preview-label">{label}</div>
      <div
        className="hospital-report-ai-preview-html"
        dangerouslySetInnerHTML={{ __html: value }}
      />
    </div>
  );
}

function HospitalReportAiDoctorsPreview({ doctors }) {
  if (!Array.isArray(doctors) || doctors.length === 0) {
    return null;
  }

  return (
    <div className="hospital-report-ai-preview-item">
      <div className="hospital-report-ai-preview-label">医師情報</div>
      <div className="hospital-report-ai-doctors-list">
        {doctors.map((doctor, index) => (
          <div className="hospital-report-ai-doctor-card" key={`${doctor.doctor_name}-${index}`}>
            <div className="hospital-report-ai-doctor-name">
              {doctor.doctor_name || `医師 ${index + 1}`}
            </div>
            <div
              className="hospital-report-ai-preview-html"
              dangerouslySetInnerHTML={{ __html: doctor.doctor_note || EMPTY_EDITOR_HTML }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function HospitalReportAiEvaluationPreview({ evaluation }) {
  if (!evaluation) {
    return null;
  }

  const busynessInputs = Object.entries(AI_BUSYNESS_INPUT_LABELS)
    .map(([fieldName, label]) => ({
      fieldName,
      label,
      value: formatAiBusynessInputValue(fieldName, evaluation.busyness_inputs?.[fieldName]),
    }))
    .filter((item) => item.value !== null);

  const entries = Object.entries(AI_EVALUATION_LABELS)
    .map(([fieldName, label]) => ({
      fieldName,
      label,
      value: formatAiEvaluationValue(fieldName, evaluation[fieldName]),
    }))
    .filter((item) => item.value !== null);

  if (entries.length === 0 && busynessInputs.length === 0) {
    return null;
  }

  return (
    <div className="hospital-report-ai-preview-item">
      <div className="hospital-report-ai-preview-label">AI が補助した全体評価</div>
      {busynessInputs.length > 0 && (
        <div className="hospital-report-ai-evaluation-grid">
          {busynessInputs.map((item) => (
            <div className="hospital-report-ai-evaluation-card" key={item.fieldName}>
              <div className="hospital-report-ai-evaluation-label">{item.label}</div>
              <div className="hospital-report-ai-evaluation-value">{item.value}</div>
            </div>
          ))}
        </div>
      )}
      <div className="hospital-report-ai-evaluation-grid">
        {entries.map((item) => (
          <div className="hospital-report-ai-evaluation-card" key={item.fieldName}>
            <div className="hospital-report-ai-evaluation-label">{item.label}</div>
            <div className="hospital-report-ai-evaluation-value">{item.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HospitalReportAiRunningIndicator({ title, description, checkpoints }) {
  return (
    <div className="hospital-report-ai-running" aria-live="polite" aria-busy="true">
      <div className="hospital-report-ai-running-top">
        <div className="hospital-report-ai-running-orbit">
          <span className="hospital-report-ai-running-core" />
          <span className="hospital-report-ai-running-dot hospital-report-ai-running-dot-1" />
          <span className="hospital-report-ai-running-dot hospital-report-ai-running-dot-2" />
          <span className="hospital-report-ai-running-dot hospital-report-ai-running-dot-3" />
        </div>
        <div>
          <div className="hospital-report-ai-running-title">{title}</div>
          <div className="hospital-report-ai-running-description">{description}</div>
        </div>
      </div>

      <div className="hospital-report-ai-running-track">
        <span className="hospital-report-ai-running-track-bar" />
      </div>

      <div className="hospital-report-ai-running-checkpoints">
        {checkpoints.map((checkpoint, index) => (
          <div className="hospital-report-ai-running-checkpoint" key={`${checkpoint}-${index}`}>
            <span className="hospital-report-ai-running-checkpoint-dot" />
            <span>{checkpoint}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * リッチテキストエディタ用コンポーネント
 */
function RichTextEditor({ value, onChange, placeholder, disabled }) {
  const quillRef = useRef(null);
  const containerRef = useRef(null);

  // Quillエディタのツールバー設定
  const modules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video', 'formula'],
      [{ header: 1 }, { header: 2 }],
      [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ direction: 'rtl' }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ align: [] }],
      ['clean']
    ]
  }), []);

  // モバイル用にツールバーを簡略化
  const mobileModules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ header: [1, 2, false] }],
      ['clean']
    ]
  }), []);

  // 画面サイズによってモジュールを切り替え
  const [isMobile, setIsMobile] = useState(false);
  const [activeModules, setActiveModules] = useState(modules);
  const [editorKey, setEditorKey] = useState(Date.now()); // 強制再レンダリング用のキー

  // 画面サイズの変更を検知するための関数
  const handleResize = useCallback(() => {
    // ブレークポイントを769pxに変更（CSSと一致させる）
    const mobile = window.innerWidth <= 769;
    if (mobile !== isMobile) {
      setIsMobile(mobile);
      // サイズ変更時にキーを更新して強制再レンダリング
      setEditorKey(Date.now());
    }
  }, [isMobile]);

  // コンポーネントのマウント時とリサイズ時に実行
  useEffect(() => {
    // 初期チェック
    handleResize();

    // リサイズイベントのリスナーを追加
    window.addEventListener('resize', handleResize);

    // クリーンアップ関数
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  // 画面サイズに応じてツールバーを変更
  useEffect(() => {
    setActiveModules(isMobile ? mobileModules : modules);
  }, [isMobile, mobileModules, modules]);

  // フォーマット指定
  const formats = [
    'bold', 'italic', 'underline', 'strike',
    'blockquote', 'code-block',
    'link', 'image', 'video', 'formula',
    'header', 'list', 'check', 'script', 'indent',
    'direction', 'size', 'color', 'background',
    'font', 'align'
  ];

  // 入力内容の変更に合わせてエディタの高さを自動調整
  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      if (editor && editor.root) {
        editor.root.style.height = 'auto';
        editor.root.style.height = editor.root.scrollHeight + 'px';
      }
    }
  }, [value]);

  // コンテナのサイズを強制的に設定
  useEffect(() => {
    if (containerRef.current) {
      // コンテナ要素を確実に表示
      containerRef.current.style.display = 'block';
      containerRef.current.style.width = '100%';
      containerRef.current.style.boxSizing = 'border-box';

      // Quillコンテナの子要素も強制的に表示
      const quilContainer = containerRef.current.querySelector('.quill');
      if (quilContainer) {
        quilContainer.style.display = 'block';
        quilContainer.style.width = '100%';
      }
    }
  }, [editorKey, isMobile]);

  // インラインスタイル
  const containerStyle = {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    margin: '0 0 1rem 0',
    position: 'relative'
  };

  // モバイル表示の場合はさらに強制的にスタイルを適用
  const mobileStyle = isMobile ? {
    minHeight: '150px',
    boxShadow: '0 0 0 1px #00a0b0'
  } : {};

  return (
    <div
      className="quill-container"
      style={{ ...containerStyle, ...mobileStyle }}
      ref={containerRef}
    >
      <ReactQuill
        key={editorKey}
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={activeModules}
        formats={formats}
        placeholder={placeholder}
        readOnly={disabled}
        className={`quill-editor ${disabled ? "quill-disabled" : ""}`}
        style={{
          width: '100%',
          display: 'block',
          minHeight: isMobile ? '150px' : '120px'
        }}
      />
    </div>
  );
}

/**
 * DoctorNoteEditor
 * 指導医ごとのコメント（申し送り）を記入するエディタ
 */
function DoctorNoteEditor({ value, onChange, placeholder, disabled }) {
  const quillRef = useRef(null);
  const containerRef = useRef(null);

  // Quillエディタのツールバー設定
  const modules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ header: [1, 2, 3, false] }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ['clean']
    ]
  }), []);

  // モバイル用に簡略化したツールバー
  const mobileModules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['clean']
    ]
  }), []);

  // 画面サイズによってモジュールを切り替え
  const [isMobile, setIsMobile] = useState(false);
  const [activeModules, setActiveModules] = useState(modules);
  const [editorKey, setEditorKey] = useState(Date.now()); // 強制再レンダリング用のキー

  // 画面サイズの変更を検知するための関数
  const handleResize = useCallback(() => {
    // ブレークポイントを769pxに変更（CSSと一致させる）
    const mobile = window.innerWidth <= 769;
    if (mobile !== isMobile) {
      setIsMobile(mobile);
      // サイズ変更時にキーを更新して強制再レンダリング
      setEditorKey(Date.now());
    }
  }, [isMobile]);

  // コンポーネントのマウント時とリサイズ時に実行
  useEffect(() => {
    // 初期チェック
    handleResize();

    // リサイズイベントのリスナーを追加
    window.addEventListener('resize', handleResize);

    // クリーンアップ関数
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  // 画面サイズに応じてツールバーを変更
  useEffect(() => {
    setActiveModules(isMobile ? mobileModules : modules);
  }, [isMobile, mobileModules, modules]);

  // フォーマット指定
  const formats = [
    'bold', 'italic', 'underline', 'strike',
    'blockquote', 'code-block',
    'link', 'image', 'video', 'formula',
    'header', 'list', 'check', 'script', 'indent',
    'direction', 'size', 'color', 'background',
    'font', 'align'
  ];

  // 入力内容の変更に合わせてエディタの高さを自動調整
  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      if (editor && editor.root) {
        editor.root.style.height = 'auto';
        editor.root.style.height = editor.root.scrollHeight + 'px';
      }
    }
  }, [value]);

  // コンテナのサイズを強制的に設定
  useEffect(() => {
    if (containerRef.current) {
      // コンテナ要素を確実に表示
      containerRef.current.style.display = 'block';
      containerRef.current.style.width = '100%';
      containerRef.current.style.boxSizing = 'border-box';

      // Quillコンテナの子要素も強制的に表示
      const quilContainer = containerRef.current.querySelector('.quill');
      if (quilContainer) {
        quilContainer.style.display = 'block';
        quilContainer.style.width = '100%';
      }
    }
  }, [editorKey, isMobile]);

  // インラインスタイル
  const containerStyle = {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    margin: '0 0 1rem 0',
    position: 'relative'
  };

  // モバイル表示の場合はさらに強制的にスタイルを適用
  const mobileStyle = isMobile ? {
    minHeight: '150px',
    boxShadow: '0 0 0 1px #00a0b0'
  } : {};

  return (
    <div
      className="quill-container"
      style={{ ...containerStyle, ...mobileStyle }}
      ref={containerRef}
    >
      <ReactQuill
        key={editorKey}
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={activeModules}
        formats={formats}
        placeholder={placeholder}
        readOnly={disabled}
        className={`quill-editor ${disabled ? "quill-disabled" : ""}`}
        style={{
          width: '100%',
          display: 'block',
          minHeight: isMobile ? '150px' : '120px'
        }}
      />
    </div>
  );
}

/**
 * HospitalReportForm
 * 申し送り記入フォーム全体
 */
function HospitalReportForm({ adminMode = false }) {
  const { userInfo } = useAuth();
  const isAdminMode = adminMode && Boolean(userInfo?.is_admin);
  // モバイル対応のための状態
  const [isMobile, setIsMobile] = useState(false);
  // モバイル表示でQuillコンポーネントが表示されなくなる問題を修正するためのフラグ
  const [rerenderedQuills, setRerenderedQuills] = useState(false);

  // ★ ステップ1: isDirty State の追加
  const [isDirty, setIsDirty] = useState(false);

  // 画面サイズの変更を検知するための関数
  const handleResize = useCallback(() => {
    // ブレークポイントを769pxに変更（CSSと一致させる）
    const mobile = window.innerWidth <= 769;
    if (mobile !== isMobile) {
      setIsMobile(mobile);

      // モバイル表示に切り替わった際、全てのQuillコンポーネントを強制的に再レンダリング
      if (mobile) {
        setRerenderedQuills(false);
        setTimeout(() => {
          setRerenderedQuills(true);
        }, 100);
      }
    }
  }, [isMobile]);

  // コンポーネントのマウント時とリサイズ時に実行
  useEffect(() => {
    // 初期チェック
    handleResize();

    // リサイズイベントのリスナーを追加
    window.addEventListener('resize', handleResize);

    // クリーンアップ関数
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  // モバイル表示時にすべてのQuill要素を強制的に表示する
  useEffect(() => {
    if (isMobile) {
      // DOMにあるすべてのQuill関連要素に直接スタイルを設定
      const applyForcedStyles = () => {
        const quillContainers = document.querySelectorAll('.quill-container');
        const quills = document.querySelectorAll('.quill');
        const editors = document.querySelectorAll('.ql-editor');
        const toolbars = document.querySelectorAll('.ql-toolbar');

        // 強制スタイル適用関数
        const forceElementStyles = (elements, styles) => {
          elements.forEach(el => {
            Object.keys(styles).forEach(prop => {
              el.style[prop] = styles[prop];
            });
          });
        };

        // コンテナに適用
        forceElementStyles(quillContainers, {
          display: 'block',
          width: '100%',
          boxSizing: 'border-box',
          visibility: 'visible',
          minHeight: '150px',
          overflow: 'visible'
        });

        // Quill要素に適用
        forceElementStyles(quills, {
          display: 'block',
          width: '100%',
          visibility: 'visible'
        });

        // エディタに適用
        forceElementStyles(editors, {
          minHeight: '100px',
          visibility: 'visible'
        });

        // ツールバーに適用
        forceElementStyles(toolbars, {
          display: 'flex',
          flexWrap: 'wrap',
          width: '100%',
          visibility: 'visible'
        });
      };

      // 初回レンダリング後、そして100ms後、500ms後に強制適用（表示されるまで複数回試行）
      applyForcedStyles();
      setTimeout(applyForcedStyles, 100);
      setTimeout(applyForcedStyles, 500);
    }
  }, [isMobile, rerenderedQuills]);

  // リッチテキストエディタ用 State
  const [schedule, setSchedule] = useSessionStorage('schedule', '<p><br></p>');
  const [training, setTraining] = useSessionStorage('training', '<p><br></p>');
  const [assignment, setAssignment] = useSessionStorage('assignment', '<p><br></p>');
  const [facility, setFacility] = useSessionStorage('facility', '<p><br></p>');
  const [other, setOther] = useSessionStorage('other', '<p><br></p>');
  const [hospitalTourText, setHospitalTourText] = useSessionStorage('hospitalTourText', '<p><br></p>');

  // 課題の有無（初期値true）
  const [hasAssignment, setHasAssignment] = useSessionStorage('hasAssignment', true);

  // 病院見学の可否
  const [hospitalTourOption, setHospitalTourOption] = useSessionStorage('hospitalTourOption', '');

  // 指導医（複数追加可）
  const [doctors, setDoctors] = useSessionStorage('doctors', [
    { id: 1, doctorName: '', doctorNote: '<p><br></p>' }
  ]);

  // 食事提供
  const [breakfast, setBreakfast] = useSessionStorage('breakfast', '');
  const [lunch, setLunch] = useSessionStorage('lunch', '');
  const [dinner, setDinner] = useSessionStorage('dinner', '');

  // ★「最終割り当て情報」を一括で保持
  const [finalAssignments, setFinalAssignments] = useState([]);

  // ターム選択(1〜7) + 自動設定用
  const [selectedTerm, setSelectedTerm] = useSessionStorage('selectedTerm', '');
  const [autoHospitalName, setAutoHospitalName] = useState('');
  const [autoDepartmentName, setAutoDepartmentName] = useState('');
  const [departmentTermCapacities, setDepartmentTermCapacities] = useState([]);
  const [cohortOptions, setCohortOptions] = useState([]);
  const [selectedCohortId, setSelectedCohortId] = useState('');
  const [selectedHospitalId, setSelectedHospitalId] = useState('');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');

  // ★ 評価項目（ラジオボタン）
  const [typicalStartTime, setTypicalStartTime] = useSessionStorage('typicalStartTime', '');
  const [typicalEndTime, setTypicalEndTime] = useSessionStorage('typicalEndTime', '');
  const [lateDaysPerWeek, setLateDaysPerWeek] = useSessionStorage('lateDaysPerWeek', '');
  const [freeStudyTime, setFreeStudyTime] = useSessionStorage('freeStudyTime', '');
  const [hasNightDuty, setHasNightDuty] = useSessionStorage('hasNightDuty', null);
  const [busyness, setBusyness] = useSessionStorage('busyness', null);
  const [assignmentBurden, setAssignmentBurden] = useSessionStorage('assignmentBurden', null);
  const [selfStudyAvailability, setSelfStudyAvailability] = useSessionStorage('selfStudyAvailability', null);
  const [handsOnOpportunity, setHandsOnOpportunity] = useSessionStorage('handsOnOpportunity', null);
  const [lodgingConvenience, setLodgingConvenience] = useSessionStorage('lodgingConvenience', null);
  const [teachingQuality, setTeachingQuality] = useSessionStorage('teachingQuality', null);
  const [questionApproachability, setQuestionApproachability] = useSessionStorage('questionApproachability', null);
  const [departmentAtmosphere, setDepartmentAtmosphere] = useSessionStorage('departmentAtmosphere', null);
  const [doctorPersonality, setDoctorPersonality] = useSessionStorage('doctorPersonality', null);
  const [carRequirement, setCarRequirement] = useSessionStorage('carRequirement', null);
  const [doctorEval, setDoctorEval] = useSessionStorage('doctorEval', null);
  const [departmentEval, setDepartmentEval] = useSessionStorage('departmentEval', null);
  const [hospitalCleanliness, setHospitalCleanliness] = useSessionStorage('hospitalCleanliness', null);
  const [lodgingCleanliness, setLodgingCleanliness] = useSessionStorage('lodgingCleanliness', null);
  const [transport, setTransport] = useSessionStorage('transport', null); // true/false

  // ★ レポートID管理用
  const [reportId, setReportId] = useState(null);

  // ロード状態を管理するステート
  const [isLoading, setIsLoading] = useState(false);

  // 初期データ取得中のローディング状態
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // 現在選択中のタームに対応する割り当て情報
  const [currentAssignment, setCurrentAssignment] = useState(null);

  // 新しいエラー状態を追加
  const [errors, setErrors] = useState({});
  const [isCopyingPrevious, setIsCopyingPrevious] = useState(false);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [copyCandidates, setCopyCandidates] = useState([]);
  const [copyCandidatesError, setCopyCandidatesError] = useState('');
  const [selectedCopyReportId, setSelectedCopyReportId] = useState(null);
  const [aiFreeformText, setAiFreeformText] = useState('');
  const [freeformAiResult, setFreeformAiResult] = useState(null);
  const [freeformAiError, setFreeformAiError] = useState('');
  const [isClassifyingFreeform, setIsClassifyingFreeform] = useState(false);
  const [freeformAiCooldown, setFreeformAiCooldown] = useState(0);
  const [refineAiResult, setRefineAiResult] = useState(null);
  const [refineAiError, setRefineAiError] = useState('');
  const [isRefiningReport, setIsRefiningReport] = useState(false);
  const [refineAiCooldown, setRefineAiCooldown] = useState(0);
  const [aiUsageStatus, setAiUsageStatus] = useState(null);
  const [isAiUsageLoading, setIsAiUsageLoading] = useState(false);
  const [aiUsageError, setAiUsageError] = useState('');

  // ★ ステップ2: markAsDirty ヘルパー関数の定義
  const markAsDirty = () => {
    if (!isLoading) {
      setIsDirty(true);
    }
  };

  const busynessInputs = useMemo(() => ({
    typical_start_time: typicalStartTime || null,
    typical_end_time: typicalEndTime || null,
    late_days_per_week: lateDaysPerWeek === '' ? null : Number(lateDaysPerWeek),
    free_study_time: freeStudyTime || null,
    has_night_duty: hasNightDuty,
  }), [
    freeStudyTime,
    hasNightDuty,
    lateDaysPerWeek,
    typicalEndTime,
    typicalStartTime,
  ]);

  const calculatedBusyness = useMemo(
    () => calculateBusyness(busynessInputs),
    [busynessInputs],
  );
  const effectiveBusyness = busyness ?? calculatedBusyness;

  useEffect(() => {
    if (freeformAiCooldown <= 0) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setFreeformAiCooldown((current) => {
        if (current <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [freeformAiCooldown]);

  useEffect(() => {
    if (refineAiCooldown <= 0) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setRefineAiCooldown((current) => {
        if (current <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [refineAiCooldown]);

  useEffect(() => {
    let isMounted = true;

    const fetchInitialData = async () => {
      setIsInitialLoading(true);

      try {
        const cohortData = await getCohorts();
        if (isMounted) {
          setCohortOptions(Array.isArray(cohortData) ? cohortData : []);
        }

        if (!isAdminMode) {
          return;
        }

        const capacityData = await getDepartmentTermCapacities();
        if (isMounted) {
          setDepartmentTermCapacities(Array.isArray(capacityData) ? capacityData : []);
        }
      } catch (error) {
        console.error('申し送りフォーム初期データの取得に失敗しました:', error);
        alert('申し送りフォームの初期データ取得に失敗しました。時間をおいて再度お試しください。');
      } finally {
        if (isMounted) {
          setIsInitialLoading(false);
        }
      }
    };

    fetchInitialData();

    return () => {
      isMounted = false;
    };
  }, [isAdminMode]);

  useEffect(() => {
    if (!isAdminMode && userInfo?.cohort_id) {
      const ownCohortId = String(userInfo.cohort_id);
      if (selectedCohortId !== ownCohortId) {
        setSelectedCohortId(ownCohortId);
      }
      return;
    }

    if (selectedCohortId) {
      return;
    }

    if (userInfo?.cohort_id) {
      setSelectedCohortId(String(userInfo.cohort_id));
      return;
    }

    const defaultCohort =
      cohortOptions.find((cohort) => cohort.is_default) ||
      cohortOptions.find((cohort) => cohort.is_active) ||
      cohortOptions[0];

    if (defaultCohort) {
      setSelectedCohortId(String(defaultCohort.id));
    }
  }, [cohortOptions, isAdminMode, selectedCohortId, setSelectedCohortId, userInfo]);

  const adminHospitalOptions = useMemo(() => {
    if (!isAdminMode || !selectedTerm) {
      return [];
    }

    const termNum = Number(selectedTerm);
    const map = new Map();
    departmentTermCapacities.forEach((item) => {
      if (item.term_id === termNum) {
        map.set(item.institution_id, item.institution_name);
      }
    });

    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name), 'ja'));
  }, [isAdminMode, selectedTerm, departmentTermCapacities]);

  const adminDepartmentOptions = useMemo(() => {
    if (!isAdminMode || !selectedTerm || !selectedHospitalId) {
      return [];
    }

    const termNum = Number(selectedTerm);
    const hospitalId = Number(selectedHospitalId);
    const map = new Map();
    departmentTermCapacities.forEach((item) => {
      if (item.term_id === termNum && item.institution_id === hospitalId) {
        map.set(item.department_id, item.department_name);
      }
    });

    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name), 'ja'));
  }, [isAdminMode, selectedTerm, selectedHospitalId, departmentTermCapacities]);

  const selectedCapacity = useMemo(() => {
    if (!isAdminMode || !selectedTerm || !selectedHospitalId || !selectedDepartmentId) {
      return null;
    }

    const termNum = Number(selectedTerm);
    const hospitalId = Number(selectedHospitalId);
    const departmentId = Number(selectedDepartmentId);

    return (
      departmentTermCapacities.find(
        (item) =>
          item.term_id === termNum &&
          item.institution_id === hospitalId &&
          item.department_id === departmentId,
      ) || null
    );
  }, [isAdminMode, selectedTerm, selectedHospitalId, selectedDepartmentId, departmentTermCapacities]);

  const selectedCohort = useMemo(
    () => cohortOptions.find((cohort) => String(cohort.id) === String(selectedCohortId)) || null,
    [cohortOptions, selectedCohortId],
  );

  const buildAiContext = useCallback(() => ({
    department_term_capacity_id:
      currentAssignment?.department_term_capacity_id || selectedCapacity?.id || null,
    cohort_id: selectedCohortId ? Number(selectedCohortId) : null,
    hospital_name: autoHospitalName || null,
    department_name: autoDepartmentName || null,
    term: selectedTerm ? Number(selectedTerm) : null,
  }), [
    autoDepartmentName,
    autoHospitalName,
    currentAssignment,
    selectedCapacity,
    selectedCohortId,
    selectedTerm,
  ]);

  const aiContext = useMemo(() => buildAiContext(), [buildAiContext]);
  const isAiUsageLocked = Boolean(aiUsageStatus?.locked);
  const aiUsageSummaryText = useMemo(() => {
    if (!aiUsageStatus) {
      return '';
    }
    return `このタームの AI 実行: ${aiUsageStatus.used_count}/${aiUsageStatus.limit} 回`;
  }, [aiUsageStatus]);

  const buildAiRefineDraft = useCallback(() => ({
    schedule,
    training,
    has_assignment: hasAssignment,
    assignment: hasAssignment ? assignment : null,
    facility,
    other,
    hospital_tour_text: hospitalTourText,
    hospital_tour_option: hospitalTourOption || null,
    doctors: doctors.map((doctor) => ({
      doctor_name: doctor.doctorName,
      doctor_note: doctor.doctorNote,
    })),
    current_evaluation: {
      busyness_inputs: {
        typical_start_time: typicalStartTime || null,
        typical_end_time: typicalEndTime || null,
        late_days_per_week: lateDaysPerWeek === '' ? null : Number(lateDaysPerWeek),
        free_study_time: freeStudyTime || null,
        has_night_duty: hasNightDuty,
      },
      busyness: busyness ?? calculatedBusyness,
      assignment_burden: assignmentBurden,
      self_study_availability: selfStudyAvailability,
      hands_on_opportunity: handsOnOpportunity,
      lodging_convenience: lodgingConvenience,
      teaching_quality: teachingQuality,
      question_approachability: questionApproachability,
      department_atmosphere: departmentAtmosphere,
      doctor_personality: doctorPersonality,
      car_requirement: carRequirement,
      breakfast: breakfast || null,
      lunch: lunch || null,
      dinner: dinner || null,
      hospital_tour_option: hospitalTourOption || null,
    },
  }), [
    assignment,
    assignmentBurden,
    doctors,
    facility,
    freeStudyTime,
    hasAssignment,
    hasNightDuty,
    handsOnOpportunity,
    hospitalTourOption,
    hospitalTourText,
    lateDaysPerWeek,
    lodgingConvenience,
    other,
    schedule,
    selfStudyAvailability,
    teachingQuality,
    questionApproachability,
    departmentAtmosphere,
    doctorPersonality,
    carRequirement,
    breakfast,
    lunch,
    dinner,
    busyness,
    calculatedBusyness,
    typicalStartTime,
    typicalEndTime,
    training,
  ]);

  const applyAiNormalizedFields = useCallback((normalized) => {
    if (!normalized) {
      return;
    }

    if (normalized.schedule && !isEditorContentEmpty(normalized.schedule)) {
      setSchedule(normalized.schedule);
    }
    if (normalized.training && !isEditorContentEmpty(normalized.training)) {
      setTraining(normalized.training);
    }
    if (normalized.facility && !isEditorContentEmpty(normalized.facility)) {
      setFacility(normalized.facility);
    }
    if (normalized.other && !isEditorContentEmpty(normalized.other)) {
      setOther(normalized.other);
    }
    if (normalized.hospital_tour_text && !isEditorContentEmpty(normalized.hospital_tour_text)) {
      setHospitalTourText(normalized.hospital_tour_text);
    }
    if (normalized.has_assignment === true) {
      setHasAssignment(true);
      if (normalized.assignment && !isEditorContentEmpty(normalized.assignment)) {
        setAssignment(normalized.assignment);
      }
    } else if (normalized.has_assignment === false) {
      setHasAssignment(false);
      setAssignment(EMPTY_EDITOR_HTML);
    } else if (normalized.assignment && !isEditorContentEmpty(normalized.assignment)) {
      setAssignment(normalized.assignment);
    }
    if (normalized.hospital_tour_option) {
      setHospitalTourOption(normalized.hospital_tour_option);
    }
    if (Array.isArray(normalized.doctors) && normalized.doctors.length > 0) {
      setDoctors(normalized.doctors.map((doctor, index) => buildDoctorState(doctor, index)));
    }
    setIsDirty(true);
  }, [
    setAssignment,
    setDoctors,
    setFacility,
    setHasAssignment,
    setHospitalTourOption,
    setHospitalTourText,
    setOther,
    setSchedule,
    setTraining,
  ]);

  const applyAiEvaluationFields = useCallback((evaluation) => {
    if (!evaluation) {
      return;
    }

    if (evaluation.busyness_inputs?.typical_start_time) {
      setTypicalStartTime(evaluation.busyness_inputs.typical_start_time);
    }
    if (evaluation.busyness_inputs?.typical_end_time) {
      setTypicalEndTime(evaluation.busyness_inputs.typical_end_time);
    }
    if (
      evaluation.busyness_inputs?.late_days_per_week !== null
      && evaluation.busyness_inputs?.late_days_per_week !== undefined
    ) {
      setLateDaysPerWeek(String(evaluation.busyness_inputs.late_days_per_week));
    }
    if (evaluation.busyness_inputs?.free_study_time) {
      setFreeStudyTime(evaluation.busyness_inputs.free_study_time);
    }
    if (
      evaluation.busyness_inputs?.has_night_duty !== null
      && evaluation.busyness_inputs?.has_night_duty !== undefined
    ) {
      setHasNightDuty(evaluation.busyness_inputs.has_night_duty);
    }
    if (evaluation.busyness !== null && evaluation.busyness !== undefined) {
      setBusyness(evaluation.busyness);
    }
    if (evaluation.car_requirement !== null && evaluation.car_requirement !== undefined) {
      setCarRequirement(evaluation.car_requirement);
    }
    if (evaluation.assignment_burden !== null && evaluation.assignment_burden !== undefined) {
      setAssignmentBurden(evaluation.assignment_burden);
    }
    if (
      evaluation.self_study_availability !== null
      && evaluation.self_study_availability !== undefined
    ) {
      setSelfStudyAvailability(evaluation.self_study_availability);
    }
    if (evaluation.hands_on_opportunity !== null && evaluation.hands_on_opportunity !== undefined) {
      setHandsOnOpportunity(evaluation.hands_on_opportunity);
    }
    if (evaluation.lodging_convenience !== null && evaluation.lodging_convenience !== undefined) {
      setLodgingConvenience(evaluation.lodging_convenience);
    }
    if (evaluation.teaching_quality !== null && evaluation.teaching_quality !== undefined) {
      setTeachingQuality(evaluation.teaching_quality);
    }
    if (
      evaluation.question_approachability !== null
      && evaluation.question_approachability !== undefined
    ) {
      setQuestionApproachability(evaluation.question_approachability);
    }
    if (
      evaluation.department_atmosphere !== null
      && evaluation.department_atmosphere !== undefined
    ) {
      setDepartmentAtmosphere(evaluation.department_atmosphere);
    }
    if (evaluation.doctor_personality !== null && evaluation.doctor_personality !== undefined) {
      setDoctorPersonality(evaluation.doctor_personality);
    }
    if (evaluation.doctor_eval !== null && evaluation.doctor_eval !== undefined) {
      setDoctorEval(evaluation.doctor_eval);
    }
    if (evaluation.department_eval !== null && evaluation.department_eval !== undefined) {
      setDepartmentEval(evaluation.department_eval);
    }
    if (evaluation.hospital_cleanliness !== null && evaluation.hospital_cleanliness !== undefined) {
      setHospitalCleanliness(evaluation.hospital_cleanliness);
    }
    if (evaluation.lodging_cleanliness !== null && evaluation.lodging_cleanliness !== undefined) {
      setLodgingCleanliness(evaluation.lodging_cleanliness);
    }
    if (evaluation.transport !== null && evaluation.transport !== undefined) {
      setTransport(evaluation.transport);
    }
    if (evaluation.breakfast) {
      setBreakfast(evaluation.breakfast);
    }
    if (evaluation.lunch) {
      setLunch(evaluation.lunch);
    }
    if (evaluation.dinner) {
      setDinner(evaluation.dinner);
    }
    if (evaluation.hospital_tour_option) {
      setHospitalTourOption(evaluation.hospital_tour_option);
    }
    setIsDirty(true);
  }, [
    setAssignmentBurden,
    setBreakfast,
    setBusyness,
    setCarRequirement,
    setDepartmentAtmosphere,
    setDepartmentEval,
    setDinner,
    setDoctorEval,
    setDoctorPersonality,
    setFreeStudyTime,
    setHandsOnOpportunity,
    setHasNightDuty,
    setHospitalCleanliness,
    setHospitalTourOption,
    setLodgingConvenience,
    setLodgingCleanliness,
    setLunch,
    setQuestionApproachability,
    setSelfStudyAvailability,
    setTeachingQuality,
    setTypicalEndTime,
    setTypicalStartTime,
    setLateDaysPerWeek,
    setTransport,
  ]);

  useEffect(() => {
    if (!isAdminMode) {
      return;
    }

    if (selectedCapacity) {
      setCurrentAssignment({ department_term_capacity_id: selectedCapacity.id });
      setAutoHospitalName(selectedCapacity.institution_name || '');
      setAutoDepartmentName(selectedCapacity.department_name || '');
    } else {
      setCurrentAssignment(null);
      setAutoHospitalName('');
      setAutoDepartmentName('');
    }
  }, [isAdminMode, selectedCapacity]);

  useEffect(() => {
    if (!isAdminMode) {
      return;
    }
    setSelectedHospitalId('');
    setSelectedDepartmentId('');
  }, [isAdminMode, selectedTerm]);

  useEffect(() => {
    if (!isAdminMode) {
      return;
    }
    setSelectedDepartmentId('');
  }, [isAdminMode, selectedHospitalId]);

  useEffect(() => {
    setFreeformAiResult(null);
    setFreeformAiError('');
    setRefineAiResult(null);
    setRefineAiError('');
    setIsCopyDialogOpen(false);
    setCopyCandidates([]);
    setCopyCandidatesError('');
    setSelectedCopyReportId(null);
  }, [selectedCohortId, selectedTerm, selectedCapacity?.id, currentAssignment?.department_term_capacity_id]);

  useEffect(() => {
    if (!selectedCohortId || !selectedTerm) {
      setAiUsageStatus(null);
      setAiUsageError('');
      setIsAiUsageLoading(false);
      return;
    }

    let isMounted = true;
    setIsAiUsageLoading(true);
    setAiUsageError('');

    getHospitalReportAiUsageStatus({
      department_term_capacity_id: aiContext.department_term_capacity_id,
      term: aiContext.term,
      cohort_id: aiContext.cohort_id,
    })
      .then((status) => {
        if (!isMounted) {
          return;
        }
        setAiUsageStatus(status);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }
        setAiUsageStatus(null);
        setAiUsageError(
          getAiErrorMessage(error, 'AI 利用回数の確認に失敗しました。時間を置いて再読み込みしてください。'),
        );
      })
      .finally(() => {
        if (isMounted) {
          setIsAiUsageLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [aiContext, selectedCohortId, selectedTerm]);

  // 初回レンダリング時に"final"ステージの割り当て情報を取得
  useEffect(() => {
    if (isAdminMode) {
      return;
    }
    async function fetchFinal() {
      setIsInitialLoading(true);
      try {
        const data = await getResults('final');
        setFinalAssignments(data);
      } catch (error) {
        console.error('最終割り当て情報の取得に失敗', error);
      } finally {
        setIsInitialLoading(false);
      }
    }
    fetchFinal();
  }, [isAdminMode]);

  // ターム選択に応じた病院名・科名の自動設定
  // ターム選択時に割り当て情報を更新
  useEffect(() => {
    if (isAdminMode) return;
    if (!selectedTerm || !finalAssignments.length) return;

    const termNum = parseInt(selectedTerm, 10);
    const foundAssignment = finalAssignments.find(item => item.term_id === termNum);
    setCurrentAssignment(foundAssignment || null);

    // 病院名・科名の自動設定
    if (foundAssignment) {
      setAutoHospitalName(foundAssignment.institution_name.split(' - ')[0] || '');
      setAutoDepartmentName(foundAssignment.institution_name.split(' - ')[1] || '');
    } else {
      setAutoHospitalName('');
      setAutoDepartmentName('');
    }
  }, [selectedTerm, finalAssignments, isAdminMode]);

  // ★ ステップ3: データロード完了時の isDirty リセット
  // ターム変更時に既存のレポートがあればフォームへ反映するための useEffect
  const resetFormStates = useCallback(() => {
    setSchedule('<p><br></p>');
    setTraining('<p><br></p>');
    setHasAssignment(true);
    setAssignment('<p><br></p>');
    setFacility('<p><br></p>');
    setOther('<p><br></p>');
    setHospitalTourText('<p><br></p>');
    setHospitalTourOption(''); // ★初期値は空文字のままでOK

    setTypicalStartTime('');
    setTypicalEndTime('');
    setLateDaysPerWeek('');
    setFreeStudyTime('');
    setHasNightDuty(null);
    setBusyness(null);
    setAssignmentBurden(null);
    setSelfStudyAvailability(null);
    setHandsOnOpportunity(null);
    setLodgingConvenience(null);
    setTeachingQuality(null);
    setQuestionApproachability(null);
    setDepartmentAtmosphere(null);
    setDoctorPersonality(null);
    setCarRequirement(null);
    setDoctorEval(null);
    setDepartmentEval(null);
    setHospitalCleanliness(null);
    setLodgingCleanliness(null);

    setBreakfast(''); // ★初期値は空文字のままでOK
    setLunch('');   // ★初期値は空文字のままでOK
    setDinner('');  // ★初期値は空文字のままでOK
    setTransport(null);

    setDoctors([{ id: 1, doctorName: '', doctorNote: '<p><br></p>' }]);
    setIsDirty(false);
  }, [setAssignment, setAssignmentBurden, setBreakfast, setBusyness, setCarRequirement, setDepartmentAtmosphere, setDepartmentEval, setDinner, setDoctorEval, setDoctorPersonality, setDoctors, setFacility, setFreeStudyTime, setHandsOnOpportunity, setHasAssignment, setHasNightDuty, setHospitalCleanliness, setHospitalTourOption, setHospitalTourText, setIsDirty, setLateDaysPerWeek, setLodgingCleanliness, setLodgingConvenience, setLunch, setOther, setQuestionApproachability, setSchedule, setSelfStudyAvailability, setTeachingQuality, setTraining, setTransport, setTypicalEndTime, setTypicalStartTime]);

  const applyReportToForm = useCallback((reportDetails, options = {}) => {
    const { keepReportId = true, dirty = false } = options;

    setReportId(keepReportId ? reportDetails.id : null);
    setSchedule(reportDetails.schedule || '<p><br></p>');
    setTraining(reportDetails.training || '<p><br></p>');
    setHasAssignment(reportDetails.has_assignment);
    setAssignment(reportDetails.assignment || '<p><br></p>');
    setFacility(reportDetails.facility || '<p><br></p>');
    setOther(reportDetails.other || '<p><br></p>');
    setHospitalTourText(reportDetails.hospital_tour_text || '<p><br></p>');
    setHospitalTourOption(reportDetails.hospital_tour_option || '');
    setTypicalStartTime(reportDetails.typical_start_time || '');
    setTypicalEndTime(reportDetails.typical_end_time || '');
    setLateDaysPerWeek(
      reportDetails.late_days_per_week === null || reportDetails.late_days_per_week === undefined
        ? ''
        : String(reportDetails.late_days_per_week),
    );
    setFreeStudyTime(reportDetails.free_study_time || '');
    setHasNightDuty(
      reportDetails.has_night_duty === null || reportDetails.has_night_duty === undefined
        ? null
        : Boolean(reportDetails.has_night_duty),
    );
    setBusyness(reportDetails.busyness);
    setAssignmentBurden(reportDetails.assignment_burden);
    setSelfStudyAvailability(reportDetails.self_study_availability);
    setHandsOnOpportunity(reportDetails.hands_on_opportunity);
    setLodgingConvenience(reportDetails.lodging_convenience ?? reportDetails.lodging_cleanliness);
    setTeachingQuality(reportDetails.teaching_quality);
    setQuestionApproachability(reportDetails.question_approachability);
    setDepartmentAtmosphere(reportDetails.department_atmosphere ?? reportDetails.department_eval);
    setDoctorPersonality(reportDetails.doctor_personality ?? reportDetails.doctor_eval);
    setCarRequirement(reportDetails.car_requirement);
    setDoctorEval(reportDetails.doctor_eval);
    setDepartmentEval(reportDetails.department_eval);
    setHospitalCleanliness(reportDetails.hospital_cleanliness);
    setLodgingCleanliness(reportDetails.lodging_cleanliness);
    setBreakfast(reportDetails.breakfast || '');
    setLunch(reportDetails.lunch || '');
    setDinner(reportDetails.dinner || '');
    setTransport(reportDetails.transport);

    const doctorsData =
      (Array.isArray(reportDetails.doctors_json) && reportDetails.doctors_json) ||
      (Array.isArray(reportDetails.doctors) && reportDetails.doctors) ||
      [];

    if (doctorsData.length > 0) {
      setDoctors(
        doctorsData.map((doctor, index) => ({
          id: index + 1,
          doctorName: doctor.doctor_name || doctor.doctorName || '',
          doctorNote: doctor.doctor_note || doctor.doctorNote || '<p><br></p>',
        })),
      );
    } else {
      setDoctors([{ id: 1, doctorName: '', doctorNote: '<p><br></p>' }]);
    }

    setIsDirty(dirty);
  }, [
    setAssignment,
    setAssignmentBurden,
    setBreakfast,
    setBusyness,
    setCarRequirement,
    setDepartmentAtmosphere,
    setDepartmentEval,
    setDinner,
    setDoctorEval,
    setDoctorPersonality,
    setDoctors,
    setFacility,
    setFreeStudyTime,
    setHandsOnOpportunity,
    setHasAssignment,
    setHasNightDuty,
    setHospitalCleanliness,
    setHospitalTourOption,
    setHospitalTourText,
    setIsDirty,
    setLateDaysPerWeek,
    setLodgingCleanliness,
    setLodgingConvenience,
    setLunch,
    setOther,
    setQuestionApproachability,
    setReportId,
    setSchedule,
    setSelfStudyAvailability,
    setTeachingQuality,
    setTraining,
    setTransport,
    setTypicalEndTime,
    setTypicalStartTime,
  ]);

  useEffect(() => {
    if (isAdminMode) {
      return;
    }
    if (!selectedTerm) {
      setAutoHospitalName('');
      setAutoDepartmentName('');
      return;
    }
    const termNum = Number(selectedTerm);
    const found = finalAssignments.find(item => item.term_id === termNum);
    if (!found) {
      setAutoHospitalName('');
      setAutoDepartmentName('');
      return;
    }
    if (found.institution_name === '未割り当て') {
      setAutoHospitalName('');
      setAutoDepartmentName('');
    } else {
      const [h, d] = found.institution_name.split(' - ');
      setAutoHospitalName(h || '');
      setAutoDepartmentName(d || '');
    }
  }, [selectedTerm, finalAssignments, resetFormStates, setReportId, setSchedule, setTraining, setHasAssignment, setAssignment, setFacility, setOther, setHospitalTourText, setHospitalTourOption, setBusyness, setCarRequirement, setDoctorEval, setDepartmentEval, setHospitalCleanliness, setLodgingCleanliness, setBreakfast, setLunch, setDinner, setTransport, setDoctors, setIsDirty, setIsLoading, isAdminMode]);

  useEffect(() => {
    async function fetchSavedReportIfExists() {
      if (!selectedTerm || !selectedCohortId) return;

      setIsLoading(true);

      let capacityId = null;

      if (isAdminMode) {
        if (!selectedCapacity) {
          resetFormStates();
          setReportId(null);
          setIsLoading(false);
          return;
        }
        capacityId = selectedCapacity.id;
      }

      if (!isAdminMode) {
        const termNum = parseInt(selectedTerm, 10);
      // finalAssignments がロードされるのを待つ必要があるかもしれない
      if (!finalAssignments || finalAssignments.length === 0) {
        // 必要であればここで finalAssignments の取得を待つか、
        // finalAssignments を useEffect の依存配列に入れて再実行を待つ
        setIsLoading(false);
        return;
      }

      const foundAssignment = finalAssignments.find(item => item.term_id === termNum);

      // 最終割り当てデータが無い or 未割り当て or capacity ID がない場合は新規モード
      if (!foundAssignment || foundAssignment.institution_name === '未割り当て' || !foundAssignment.department_term_capacity_id) {
        resetFormStates();
        setReportId(null);
        setIsLoading(false);
        return;
      }

      capacityId = foundAssignment.department_term_capacity_id;
      }

      try {
        // ★ 新しいAPI関数を呼び出す
        const reportDetails = await getHospitalReportByCapacityId(capacityId, {
          cohort_id: selectedCohortId,
        });

        if (reportDetails && reportDetails.id) {
          applyReportToForm(reportDetails);
        } else {
          resetFormStates();
          setReportId(null);
        }
      } catch (err) {
        console.error('🔴 ERROR: 既存レポートの取得に失敗:', err);
        resetFormStates();
        setReportId(null);
        // ユーザーにエラーを通知する (例: alert or エラーstate)
        alert("レポートデータの読み込みに失敗しました。");
      } finally {
        setIsLoading(false);
      }
    }

    fetchSavedReportIfExists();
    // finalAssignments も依存配列に追加
  }, [selectedTerm, selectedCohortId, finalAssignments, resetFormStates, setReportId, setIsLoading, selectedCapacity, isAdminMode, applyReportToForm]);

  // ★ ステップ2: State 更新ハンドラの修正 (markAsDirty 呼び出し)
  const handleScheduleChange = (value) => {
    setSchedule(value);
    markAsDirty();
  };
  const handleTrainingChange = (value) => {
    setTraining(value);
    markAsDirty();
  };
  const handleAssignmentChange = (value) => {
    setAssignment(value);
    markAsDirty();
  };
  const handleFacilityChange = (value) => {
    setFacility(value);
    markAsDirty();
  };
  const handleOtherChange = (value) => {
    setOther(value);
    markAsDirty();
  };
  const handleHospitalTourTextChange = (value) => {
    setHospitalTourText(value);
    markAsDirty();
  };
  const handleHasAssignmentChange = (value) => {
    setHasAssignment(value);
    markAsDirty();
  };
  const handleHospitalTourOptionChange = (e) => {
    setHospitalTourOption(e.target.value);
    markAsDirty();
  };
  const handleBreakfastChange = (e) => {
    setBreakfast(e.target.value);
    markAsDirty();
  };
  const handleLunchChange = (e) => {
    setLunch(e.target.value);
    markAsDirty();
  };
  const handleDinnerChange = (e) => {
    setDinner(e.target.value);
    markAsDirty();
  };

  /**
   * 指導医追加ボタン
   */
  const addDoctorBox = () => {
    setDoctors([
      ...doctors,
      { id: doctors.length + 1, doctorName: '', doctorNote: '<p><br></p>' }
    ]);
    markAsDirty(); // ★
  };

  /**
   * 指導医情報の変更ハンドラ
   */
  const handleDoctorChange = (index, field, value) => {
    const updated = [...doctors];
    updated[index][field] = value;
    setDoctors(updated);
    markAsDirty(); // ★
  };

  /**
   * 指導医削除機能
   */
  const removeDoctorBox = (index) => {
    // 少なくとも1つの医師情報カードを残す
    if (doctors.length <= 1) {
      return;
    }
    const updated = [...doctors];
    updated.splice(index, 1);
    setDoctors(updated);
    markAsDirty(); // ★
  };

  // --- 評価項目(ラジオボタン) ---
  const handleRadioChange = (e, setState) => {
    const value = parseInt(e.target.value, 10);
    setState(isNaN(value) ? null : value);
    markAsDirty(); // ★
  };

  const handleBusyInputChange = (setter) => (e) => {
    setter(e.target.value);
    setBusyness(null);
    markAsDirty();
  };

  const handleBusyBooleanChange = (setter) => (e) => {
    setter(e.target.value === 'yes');
    setBusyness(null);
    markAsDirty();
  };

  const getEvaluationFieldValue = (fieldKey) => {
    switch (fieldKey) {
      case 'assignmentBurden':
        return assignmentBurden;
      case 'selfStudyAvailability':
        return selfStudyAvailability;
      case 'handsOnOpportunity':
        return handsOnOpportunity;
      case 'lodgingConvenience':
        return lodgingConvenience;
      case 'teachingQuality':
        return teachingQuality;
      case 'questionApproachability':
        return questionApproachability;
      case 'departmentAtmosphere':
        return departmentAtmosphere;
      case 'doctorPersonality':
        return doctorPersonality;
      case 'carRequirement':
        return carRequirement;
      default:
        return null;
    }
  };

  const getEvaluationFieldSetter = (fieldKey) => {
    switch (fieldKey) {
      case 'assignmentBurden':
        return setAssignmentBurden;
      case 'selfStudyAvailability':
        return setSelfStudyAvailability;
      case 'handsOnOpportunity':
        return setHandsOnOpportunity;
      case 'lodgingConvenience':
        return setLodgingConvenience;
      case 'teachingQuality':
        return setTeachingQuality;
      case 'questionApproachability':
        return setQuestionApproachability;
      case 'departmentAtmosphere':
        return setDepartmentAtmosphere;
      case 'doctorPersonality':
        return setDoctorPersonality;
      case 'carRequirement':
        return setCarRequirement;
      default:
        return null;
    }
  };

  /**
   * ペイロード構築関数（一時保存・PDF出力共通）
   */
  const buildPayload = useCallback(() => {
    // 編集中の医師情報をAPIの期待する形式に変換
    const doctorsToSend = doctors.map(doc => ({
      doctor_name: doc.doctorName,
      doctor_note: doc.doctorNote
    }));

    // ★ 空文字を null に変換するヘルパー関数
    const emptyStringToNull = (value) => (value === '' ? null : value);

    return {
      // すでに保存済みのレポートのIDがあれば含める（更新モードになる）
      id: reportId || undefined,
      departmentTermCapacityId:
        currentAssignment?.department_term_capacity_id || selectedCapacity?.id,
      cohortId: selectedCohortId ? Number(selectedCohortId) : null,

      // 各入力フィールドの値
      schedule: schedule,
      training: training,
      has_assignment: hasAssignment,
      assignment: hasAssignment ? assignment : null,
      facility: facility,
      other: other,
      hospital_tour_text: hospitalTourText,
      // ★ hospitalTourOption: 空文字ならnullに変換
      hospital_tour_option: emptyStringToNull(hospitalTourOption),

      typical_start_time: emptyStringToNull(typicalStartTime),
      typical_end_time: emptyStringToNull(typicalEndTime),
      late_days_per_week: lateDaysPerWeek === '' ? null : Number(lateDaysPerWeek),
      free_study_time: emptyStringToNull(freeStudyTime),
      has_night_duty: hasNightDuty,
      busyness: busyness ?? calculatedBusyness,
      assignment_burden: assignmentBurden,
      self_study_availability: selfStudyAvailability,
      hands_on_opportunity: handsOnOpportunity,
      lodging_convenience: lodgingConvenience,
      teaching_quality: teachingQuality,
      question_approachability: questionApproachability,
      department_atmosphere: departmentAtmosphere,
      doctor_personality: doctorPersonality,
      car_requirement: carRequirement, // 数値またはnullなので変換不要
      doctor_eval: doctorEval, // 数値またはnullなので変換不要
      department_eval: departmentEval, // 数値またはnullなので変換不要
      hospital_cleanliness: hospitalCleanliness, // 数値またはnullなので変換不要
      lodging_cleanliness: lodgingCleanliness, // 数値またはnullなので変換不要

      // ★ breakfast, lunch, dinner: 空文字ならnullに変換
      breakfast: emptyStringToNull(breakfast),
      lunch: emptyStringToNull(lunch),
      dinner: emptyStringToNull(dinner),

      transport: transport, // booleanまたはnullなので変換不要

      // バックエンドでは doctors_json フィールドとして保存されるが、
      // API上は doctors という名前で送信（バックエンドで変換される）
      doctors: doctorsToSend
    };
  }, [
    assignment,
    assignmentBurden,
    breakfast,
    busyness,
    carRequirement,
    calculatedBusyness,
    currentAssignment,
    departmentAtmosphere,
    departmentEval,
    dinner,
    doctorEval,
    doctorPersonality,
    doctors,
    facility,
    freeStudyTime,
    hasAssignment,
    handsOnOpportunity,
    hasNightDuty,
    hospitalCleanliness,
    hospitalTourOption,
    hospitalTourText,
    lateDaysPerWeek,
    lodgingCleanliness,
    lodgingConvenience,
    lunch,
    other,
    questionApproachability,
    reportId,
    schedule,
    selectedCapacity,
    selectedCohortId,
    selfStudyAvailability,
    teachingQuality,
    training,
    transport,
    typicalEndTime,
    typicalStartTime,
  ]);

  const preparePayloadForSubmission = useCallback(async () => {
    const payload = buildPayload();
    return prepareHospitalReportPayloadImages(payload, {
      capacityId: payload.departmentTermCapacityId,
      cohortId: payload.cohortId,
    });
  }, [buildPayload]);

  const handleClassifyFreeform = useCallback(async () => {
    if (!aiFreeformText.trim()) {
      setFreeformAiError('自由入力本文を入力してください。');
      return;
    }
    if (isAiUsageLocked) {
      setFreeformAiError('このタームでの AI 実行回数は上限に達しています。');
      return;
    }
    if (freeformAiCooldown > 0) {
      setFreeformAiError(`連続実行を防ぐため、あと ${freeformAiCooldown} 秒お待ちください。`);
      return;
    }

    setIsClassifyingFreeform(true);
    setFreeformAiError('');
    setFreeformAiResult(null);

    try {
      const response = await classifyHospitalReportFreeform({
        freeform_text: aiFreeformText.trim(),
        context: aiContext,
      });
      setFreeformAiResult(response);
      setAiUsageStatus(response.usage || null);
    } catch (error) {
      console.error('AI freeform classification failed:', error);
      setFreeformAiError(
        getAiErrorMessage(error, 'AI による自動分類に失敗しました。時間をおいて再実行してください。'),
      );
    } finally {
      setIsClassifyingFreeform(false);
      setFreeformAiCooldown(AI_ACTION_COOLDOWN_SECONDS);
    }
  }, [aiContext, aiFreeformText, freeformAiCooldown, isAiUsageLocked]);

  const handleRefineWithAi = useCallback(async () => {
    if (isAiUsageLocked) {
      setRefineAiError('このタームでの AI 実行回数は上限に達しています。');
      return;
    }
    if (refineAiCooldown > 0) {
      setRefineAiError(`連続実行を防ぐため、あと ${refineAiCooldown} 秒お待ちください。`);
      return;
    }

    setIsRefiningReport(true);
    setRefineAiError('');
    setRefineAiResult(null);

    try {
      const response = await refineHospitalReportDraft({
        report_draft: buildAiRefineDraft(),
        context: aiContext,
      });
      setRefineAiResult(response);
      setAiUsageStatus(response.usage || null);
    } catch (error) {
      console.error('AI report refinement failed:', error);
      setRefineAiError(
        getAiErrorMessage(error, 'AI 補正に失敗しました。時間をおいて再実行してください。'),
      );
    } finally {
      setIsRefiningReport(false);
      setRefineAiCooldown(AI_ACTION_COOLDOWN_SECONDS);
    }
  }, [aiContext, buildAiRefineDraft, isAiUsageLocked, refineAiCooldown]);

  const handleApplyFreeformAi = useCallback(() => {
    if (!freeformAiResult?.normalized) {
      return;
    }

    applyAiNormalizedFields(freeformAiResult.normalized);
    applyAiEvaluationFields(freeformAiResult.evaluation);
    setFreeformAiResult(null);
  }, [applyAiEvaluationFields, applyAiNormalizedFields, freeformAiResult]);

  const handleApplyRefineAi = useCallback(() => {
    if (!refineAiResult) {
      return;
    }

    applyAiNormalizedFields(refineAiResult.refined);
    applyAiEvaluationFields(refineAiResult.evaluation);
    setRefineAiResult(null);
  }, [applyAiEvaluationFields, applyAiNormalizedFields, refineAiResult]);

  const handleOpenCopyDialog = useCallback(async () => {
    const capacityId =
      currentAssignment?.department_term_capacity_id || selectedCapacity?.id;

    if (!capacityId || !selectedCohortId) {
      alert('コホートと配属先を選択してから前コホートのコピーを実行してください。');
      return;
    }

    setIsCopyingPrevious(true);
    setCopyCandidatesError('');
    try {
      const candidates = await getHospitalReportCopyCandidates(
        capacityId,
        Number(selectedCohortId),
      );
      const previousReport = candidates[0] || null;
      if (!Array.isArray(candidates) || candidates.length === 0) {
        setCopyCandidates([]);
        setCopyCandidatesError('コピー可能な既存レポートは見つかりませんでした。');
        setIsCopyDialogOpen(true);
        return;
      }
      setCopyCandidates(candidates);
      setIsCopyDialogOpen(true);
      return;
      // eslint-disable-next-line no-unreachable
      if (false) {
        alert('前コホートの申し送りは見つかりませんでした。');
        return;
      }

      applyReportToForm(candidates[0], { keepReportId: false, dirty: true });
      alert(
        `${previousReport.cohort_name || '前コホート'} の申し送りを現在のコホート向けフォームにコピーしました。`,
      );
    } catch (error) {
      console.error('前コホート申し送りのコピーに失敗しました:', error);
      alert('前コホートの申し送りコピーに失敗しました。');
    } finally {
      setIsCopyingPrevious(false);
    }
  }, [applyReportToForm, currentAssignment, selectedCapacity, selectedCohortId]);

  // ★ ステップ3: 保存成功時の isDirty リセット
  // ★ 一時保存機能
  const handleCopyFromExistingReport = useCallback(async (reportIdToCopy) => {
    if (!reportIdToCopy) {
      return;
    }

    setSelectedCopyReportId(reportIdToCopy);
    setCopyCandidatesError('');

    try {
      const sourceReport = await getHospitalReportById(reportIdToCopy);
      applyReportToForm(sourceReport, { keepReportId: false, dirty: true });
      setIsCopyDialogOpen(false);
      setSelectedCopyReportId(null);
      alert(
        `${sourceReport.cohort_name || '既存コホート'} / 第${sourceReport.term || '-'}タームのレポート内容をコピーしました。`,
      );
    } catch (error) {
      console.error('既存レポートのコピーに失敗しました:', error);
      setCopyCandidatesError('選択したレポートの読み込みに失敗しました。');
      setSelectedCopyReportId(null);
    }
  }, [applyReportToForm]);

  const handleTempSave = async (event) => {
    // フォームのデフォルト送信動作を防止（ページ遷移を防ぐ）
    if (event) {
      event.preventDefault();
    }

    // エラー状態をリセット
    setErrors({});

    // タイムアウトプロミスを作成するヘルパー関数
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    // ローディング表示を開始
    setIsLoading(true);

    try {
      // 短い遅延を設けて、認証状態が更新される時間を確保
      await delay(800);

      if (!selectedCohortId) {
        alert('コホートを選択してください。');
        setIsLoading(false);
        return;
      }

      if (!(currentAssignment?.department_term_capacity_id || selectedCapacity?.id)) {
        alert('タームと病院・診療科を選択してください。');
        setIsLoading(false);
        return;
      }

      const payload = await preparePayloadForSubmission();
      const response = await createOrUpdateHospitalReport(payload);

      // 成功したらレポートIDを更新
      setReportId(response.id);
      setIsDirty(false); // ★ 保存成功時に解除
      alert('一時保存が完了しました');
    } catch (err) {
      console.error('レポート保存エラー:', err);

      // 認証エラーの場合の特別な処理
      if (err.response?.status === 401) {
        // 認証エラーの場合、ユーザーに通知
        alert('認証エラーが発生しました。再度ログインしてください。');
        // ここでログインページにリダイレクトすることも可能
        // window.location.href = '/login';
        return;
      }

      // バックエンドからのエラーメッセージがある場合はそれを表示
      if (err.response?.data?.detail) {
        alert(`保存エラー: ${err.response.data.detail}`);
      } else {
        alert('一時保存に失敗しました');
      }
    } finally {
      // 処理完了後、ローディング表示を終了
      setIsLoading(false);
    }
  };

  // ★ PDF出力ボタン押下時の処理（新規タブでPDFを開く）
  const handlePDFOutput = async () => {
    // エラー状態をリセット
    setErrors({});

    // エディタ内容が空（<p><br></p>のみ）かどうかチェック
    const isEditorEmpty = (content) => {
      return !content || content === '<p><br></p>' || content === '<p></p>';
    };

    // 入力検証 - PDF出力ではその他を除くすべてのフィールドを検証
    const validationErrors = {};

    // ターム選択の検証
    if (!selectedTerm) {
      validationErrors.selectedTerm = "タームを選択してください";
    }
    if (!selectedCohortId) {
      validationErrors.selectedCohort = "コホートを選択してください";
    }

    if (isAdminMode) {
      if (!selectedHospitalId) {
        validationErrors.selectedHospital = "病院を選択してください";
      }
      if (!selectedDepartmentId) {
        validationErrors.selectedDepartment = "診療科を選択してください";
      }
    }

    // 全体評価の検証
    if (!typicalStartTime) {
      validationErrors.typicalStartTime = "「始業時刻」を入力してください";
    }
    if (!typicalEndTime) {
      validationErrors.typicalEndTime = "「終業時刻」を入力してください";
    }
    if (lateDaysPerWeek === '') {
      validationErrors.lateDaysPerWeek = "「17時以降になる日数」を選択してください";
    }
    if (!freeStudyTime) {
      validationErrors.freeStudyTime = "「自習時間」を選択してください";
    }
    if (hasNightDuty === null) {
      validationErrors.hasNightDuty = "「当直・夜遅い対応」を選択してください";
    }
    if (!effectiveBusyness) {
      validationErrors.busyness = "忙しさを判定するための情報を入力してください";
    }
    if (!assignmentBurden) {
      validationErrors.assignmentBurden = "「課題・発表の負担度」の評価を選択してください";
    }
    if (!selfStudyAvailability) {
      validationErrors.selfStudyAvailability = "「国試勉強・自習時間の確保」の評価を選択してください";
    }
    if (!handsOnOpportunity) {
      validationErrors.handsOnOpportunity = "「手技・実践機会の多さ」の評価を選択してください";
    }
    if (!teachingQuality) {
      validationErrors.teachingQuality = "「教育の質」の評価を選択してください";
    }
    if (!questionApproachability) {
      validationErrors.questionApproachability = "「質問のしやすさ」の評価を選択してください";
    }
    if (!departmentAtmosphere) {
      validationErrors.departmentAtmosphere = "「医局の雰囲気」の評価を選択してください";
    }
    if (!doctorPersonality) {
      validationErrors.doctorPersonality = "「医師の人柄」の評価を選択してください";
    }
    if (!lodgingConvenience) {
      validationErrors.lodgingConvenience = "「宿舎の利便性・快適さ」の評価を選択してください";
    }
    if (!carRequirement) {
      validationErrors.carRequirement = "「車の必要性」の評価を選択してください";
    }

    // 病院見学オプションの検証
    if (!hospitalTourOption) {
      validationErrors.hospitalTourOption = "病院見学の可否を選択してください";
    }

    // 病院見学テキストの検証
    if (isEditorEmpty(hospitalTourText)) {
      validationErrors.hospitalTourText = "病院見学に関する情報を入力してください";
    }

    // 食事オプションの検証
    if (!breakfast) {
      validationErrors.breakfast = "朝食の提供状況を選択してください";
    }
    if (!lunch) {
      validationErrors.lunch = "昼食の提供状況を選択してください";
    }
    if (!dinner) {
      validationErrors.dinner = "夕食の提供状況を選択してください";
    }

    // 医師情報の検証
    const doctorErrors = [];
    doctors.forEach((doctor, index) => {
      const doctorError = {};
      if (!doctor.doctorName || doctor.doctorName.trim() === '') {
        doctorError.doctorName = "医師名を入力してください";
      }
      if (isEditorEmpty(doctor.doctorNote)) {
        doctorError.doctorNote = "医師の説明を入力してください";
      }
      if (Object.keys(doctorError).length > 0) {
        doctorErrors[index] = doctorError;
      }
    });
    if (doctorErrors.length > 0) {
      validationErrors.doctors = doctorErrors;
    }

    if (isEditorEmpty(schedule)) {
      validationErrors.schedule = "スケジュールを入力してください";
    }

    if (isEditorEmpty(training)) {
      validationErrors.training = "実習内容を入力してください";
    }

    // 課題内容の検証（課題ありの場合のみ検証）
    if (hasAssignment && isEditorEmpty(assignment)) {
      validationErrors.assignment = "課題内容を入力してください";
    }

    if (isEditorEmpty(facility)) {
      validationErrors.facility = "設備・宿泊に関する情報を入力してください";
    }

    // 注：「その他」フィールドは検証しない（空白可）

    // バリデーションエラーがある場合、処理を中止
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // エラーメッセージをトップに表示するためにスクロール
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // ★ 登録処理開始時にローディング表示
    setIsLoading(true);
    const payload = await preparePayloadForSubmission();
    try {
      const updatedReport = await createOrUpdateHospitalReport(payload);
      setReportId(updatedReport.id);
      setIsDirty(false); // ★ 保存成功時に解除
      const pdfBlob = await getHospitalReportPDF(updatedReport.id);
      const blobUrl = URL.createObjectURL(pdfBlob);
      window.open(blobUrl, '_blank');
    } catch (err) {
      console.error('PDF出力処理失敗:', err);
      // バックエンドからのエラーメッセージがある場合はそれを表示
      if (err.response?.data?.detail) {
        alert(`PDF出力エラー: ${err.response.data.detail}`);
      } else {
        alert('PDFを出力できませんでした。');
      }
    } finally {
      // ★ 登録処理完了後にローディング解除
      setIsLoading(false);
    }
  };

  // ★ ステップ3: isDirty リセット処理の追加

  // ★ ステップ4: ブラウザ離脱時の警告 (`beforeunload`)
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isDirty) {
        event.preventDefault();
        event.returnValue = ''; // Chrome ではこれが必要
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // コンポーネントのアンマウント時にリスナーを削除
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]); // isDirty の値が変わるたびにリスナーの挙動が変わるようにする

  // ★ ステップ5: React Router 内部遷移時の警告 (`useBlocker`)
  const blocker = useBlocker(isDirty);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      const leave = window.confirm(
        '編集中の内容がありますが、ページを移動しますか？変更は保存されません。'
      );

      if (leave) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker]);

  // 初期データ取得中はローディング表示
  if (isInitialLoading) {
    return <LoadingSpinner text="申し送りフォームを準備中..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 hospital-report-form-container">
      <div className="max-w-[1200px] mx-auto p-8 bg-white rounded-lg shadow-lg">
        <h1 className="text-center mb-8 text-4xl font-extrabold text-gray-900">
          申し送り記入フォーム
        </h1>

        {/* 注意書き */}
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 mb-6 rounded-md">
          <p className="flex items-center">
            <span className="mr-2">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <span className="font-medium">必ず一次保存または登録してください</span>
          </p>
        </div>

        {/* ロード中のオーバーレイ表示 */}
        {isLoading && <LoadingSpinner text="処理中..." />}

        <form onSubmit={handleTempSave}>
          <section className="hospital-report-ai-section">
            <div className="hospital-report-ai-header">
              <div>
                <h2 className="hospital-report-ai-title">AI 補助</h2>
                <p className="hospital-report-ai-description">
                  自由入力の分類提案と、現在のフォーム内容の整文・全体評価補助を利用できます。
                </p>
              </div>
              <div className="hospital-report-ai-context">
                <span>{selectedCohort?.name || 'コホート未選択'}</span>
                <span>{autoHospitalName || '病院未選択'}</span>
                <span>{autoDepartmentName || '診療科未選択'}</span>
              </div>
            </div>

            <div className="hospital-report-ai-usage-panel">
              <div className="hospital-report-ai-usage-title">AI 実行回数</div>
              {isAiUsageLoading ? (
                <div className="hospital-report-ai-usage-text">残回数を確認しています...</div>
              ) : aiUsageStatus ? (
                <>
                  <div className="hospital-report-ai-usage-text">
                    {aiUsageSummaryText} / 残り {aiUsageStatus.remaining_count} 回
                  </div>
                  {aiUsageStatus.locked && (
                    <div className="hospital-report-ai-usage-lock">
                      このタームでは上限に達したため、リロードしても AI は再実行できません。
                    </div>
                  )}
                </>
              ) : (
                <div className="hospital-report-ai-usage-text">
                  タームとコホートが確定すると残回数を表示します。
                </div>
              )}
              {aiUsageError && (
                <div className="hospital-report-ai-error">{aiUsageError}</div>
              )}
            </div>

            <div className="hospital-report-ai-grid">
              <div className="hospital-report-ai-card">
                <div className="hospital-report-ai-card-header">
                  <div>
                    <h3 className="hospital-report-ai-card-title">AI 自動分類</h3>
                    <p className="hospital-report-ai-card-description">
                      完全フリー入力から、既存フォームの各項目へ振り分けた提案を作成します。
                    </p>
                  </div>
                  <Sparkles className="hospital-report-ai-card-icon" />
                </div>

                <textarea
                  className="hospital-report-ai-textarea"
                  value={aiFreeformText}
                  onChange={(event) => setAiFreeformText(event.target.value)}
                  placeholder="自由入力本文をここにまとめて入力してください。AI が申し送り項目ごとに整理します。"
                  rows={8}
                  disabled={isClassifyingFreeform || isAiUsageLocked}
                />

                {freeformAiError && (
                  <div className="hospital-report-ai-error">{freeformAiError}</div>
                )}

                <div className="hospital-report-ai-actions">
                  <button
                    type="button"
                    className="hospital-report-ai-primary"
                    onClick={handleClassifyFreeform}
                    disabled={isClassifyingFreeform || freeformAiCooldown > 0 || isAiUsageLocked || isAiUsageLoading}
                  >
                    {isClassifyingFreeform ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4" />
                        分類中...
                      </>
                    ) : freeformAiCooldown > 0 ? (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        {freeformAiCooldown}秒待機
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        AIで分類
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="hospital-report-ai-secondary"
                    onClick={() => {
                      setFreeformAiResult(null);
                      setFreeformAiError('');
                    }}
                    disabled={isClassifyingFreeform && !freeformAiResult}
                  >
                    <X className="h-4 w-4" />
                    キャンセル
                  </button>
                </div>

                {freeformAiCooldown > 0 && !isClassifyingFreeform && (
                  <div className="hospital-report-ai-cooldown">
                    連続実行を防ぐため、あと {freeformAiCooldown} 秒で再実行できます。
                  </div>
                )}

                {isClassifyingFreeform && (
                  <HospitalReportAiRunningIndicator
                    title="AI が自由入力を解析しています"
                    description="文章を読み取り、申し送り項目と全体評価候補に整理しています。"
                    checkpoints={[
                      '本文の要点を抽出',
                      '項目ごとに分類',
                      '評価候補を作成',
                    ]}
                  />
                )}

                {freeformAiResult && (
                  <div className="hospital-report-ai-result">
                    <HospitalReportAiWarnings warnings={freeformAiResult.warnings} />
                    {Object.entries(AI_SECTION_LABELS).map(([fieldName, label]) => (
                      <HospitalReportAiHtmlPreview
                        key={fieldName}
                        label={label}
                        value={freeformAiResult.normalized?.[fieldName]}
                      />
                    ))}
                    {freeformAiResult.normalized?.has_assignment !== null &&
                      freeformAiResult.normalized?.has_assignment !== undefined && (
                        <div className="hospital-report-ai-preview-item">
                          <div className="hospital-report-ai-preview-label">課題有無</div>
                          <div className="hospital-report-ai-inline-value">
                            {freeformAiResult.normalized.has_assignment ? 'あり' : 'なし'}
                          </div>
                        </div>
                      )}
                    {freeformAiResult.normalized?.hospital_tour_option && (
                      <div className="hospital-report-ai-preview-item">
                        <div className="hospital-report-ai-preview-label">病院見学可否</div>
                        <div className="hospital-report-ai-inline-value">
                          {AI_TOUR_OPTION_LABELS[freeformAiResult.normalized.hospital_tour_option]
                            || freeformAiResult.normalized.hospital_tour_option}
                        </div>
                      </div>
                    )}
                    <HospitalReportAiDoctorsPreview doctors={freeformAiResult.normalized?.doctors} />
                    <HospitalReportAiEvaluationPreview evaluation={freeformAiResult.evaluation} />

                    <div className="hospital-report-ai-actions">
                      <button
                        type="button"
                        className="hospital-report-ai-primary"
                        onClick={handleApplyFreeformAi}
                      >
                        AI 提案を反映
                      </button>
                      <button
                        type="button"
                        className="hospital-report-ai-secondary"
                        onClick={handleClassifyFreeform}
                        disabled={isClassifyingFreeform || freeformAiCooldown > 0 || isAiUsageLocked || isAiUsageLoading}
                      >
                        <RefreshCw className="h-4 w-4" />
                        {freeformAiCooldown > 0 ? `${freeformAiCooldown}秒待機` : '再実行'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="hospital-report-ai-card">
                <div className="hospital-report-ai-card-header">
                  <div>
                    <h3 className="hospital-report-ai-card-title">AI 補正</h3>
                    <p className="hospital-report-ai-card-description">
                      現在のフォーム内容を整文し、全体評価も補助提案します。
                    </p>
                  </div>
                  <Sparkles className="hospital-report-ai-card-icon" />
                </div>

                <div className="hospital-report-ai-note">
                  保存は行いません。結果を確認してから既存フォームへ反映できます。
                </div>

                {refineAiError && (
                  <div className="hospital-report-ai-error">{refineAiError}</div>
                )}

                <div className="hospital-report-ai-actions">
                  <button
                    type="button"
                    className="hospital-report-ai-primary"
                    onClick={handleRefineWithAi}
                    disabled={isRefiningReport || refineAiCooldown > 0 || isAiUsageLocked || isAiUsageLoading}
                  >
                    {isRefiningReport ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4" />
                        補正中...
                      </>
                    ) : refineAiCooldown > 0 ? (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        {refineAiCooldown}秒待機
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        AI 補正を実行
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="hospital-report-ai-secondary"
                    onClick={() => {
                      setRefineAiResult(null);
                      setRefineAiError('');
                    }}
                    disabled={isRefiningReport && !refineAiResult}
                  >
                    <X className="h-4 w-4" />
                    キャンセル
                  </button>
                </div>

                {refineAiCooldown > 0 && !isRefiningReport && (
                  <div className="hospital-report-ai-cooldown">
                    連続実行を防ぐため、あと {refineAiCooldown} 秒で再実行できます。
                  </div>
                )}

                {isRefiningReport && (
                  <HospitalReportAiRunningIndicator
                    title="AI がフォーム内容を補正しています"
                    description="文章の整文と、全体評価の見直し候補を順番に生成しています。"
                    checkpoints={[
                      '本文を整文',
                      '忙しさと評価を推定',
                      '反映候補を組み立て',
                    ]}
                  />
                )}

                {refineAiResult && (
                  <div className="hospital-report-ai-result">
                    <HospitalReportAiWarnings warnings={refineAiResult.warnings} />
                    {Object.entries(AI_SECTION_LABELS).map(([fieldName, label]) => (
                      <HospitalReportAiHtmlPreview
                        key={fieldName}
                        label={label}
                        value={refineAiResult.refined?.[fieldName]}
                      />
                    ))}
                    <HospitalReportAiDoctorsPreview doctors={refineAiResult.refined?.doctors} />
                    <HospitalReportAiEvaluationPreview evaluation={refineAiResult.evaluation} />

                    <div className="hospital-report-ai-actions">
                      <button
                        type="button"
                        className="hospital-report-ai-primary"
                        onClick={handleApplyRefineAi}
                      >
                        AI 提案を反映
                      </button>
                      <button
                        type="button"
                        className="hospital-report-ai-secondary"
                        onClick={handleRefineWithAi}
                        disabled={isRefiningReport || refineAiCooldown > 0 || isAiUsageLocked || isAiUsageLoading}
                      >
                        <RefreshCw className="h-4 w-4" />
                        {refineAiCooldown > 0 ? `${refineAiCooldown}秒待機` : '再実行'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
          {/* バリデーションエラーメッセージ表示 */}
          {Object.keys(errors).length > 0 && (
            <div className="bg-red-50 border border-red-300 text-red-800 p-4 mb-6 rounded-md">
              <h4 className="text-lg font-semibold mb-2">以下の情報を入力してください：</h4>
              <ul className="list-disc pl-5 space-y-1">
                {errors.selectedTerm && (
                  <li>{errors.selectedTerm}</li>
                )}
                {errors.selectedCohort && (
                  <li>{errors.selectedCohort}</li>
                )}
                {errors.selectedHospital && (
                  <li>{errors.selectedHospital}</li>
                )}
                {errors.selectedDepartment && (
                  <li>{errors.selectedDepartment}</li>
                )}
                {errors.typicalStartTime && (
                  <li>{errors.typicalStartTime}</li>
                )}
                {errors.typicalEndTime && (
                  <li>{errors.typicalEndTime}</li>
                )}
                {errors.lateDaysPerWeek && (
                  <li>{errors.lateDaysPerWeek}</li>
                )}
                {errors.freeStudyTime && (
                  <li>{errors.freeStudyTime}</li>
                )}
                {errors.hasNightDuty && (
                  <li>{errors.hasNightDuty}</li>
                )}
                {errors.busyness && (
                  <li>{errors.busyness}</li>
                )}
                {errors.assignmentBurden && (
                  <li>{errors.assignmentBurden}</li>
                )}
                {errors.selfStudyAvailability && (
                  <li>{errors.selfStudyAvailability}</li>
                )}
                {errors.handsOnOpportunity && (
                  <li>{errors.handsOnOpportunity}</li>
                )}
                {errors.teachingQuality && (
                  <li>{errors.teachingQuality}</li>
                )}
                {errors.questionApproachability && (
                  <li>{errors.questionApproachability}</li>
                )}
                {errors.departmentAtmosphere && (
                  <li>{errors.departmentAtmosphere}</li>
                )}
                {errors.doctorPersonality && (
                  <li>{errors.doctorPersonality}</li>
                )}
                {errors.lodgingConvenience && (
                  <li>{errors.lodgingConvenience}</li>
                )}
                {errors.carRequirement && (
                  <li>{errors.carRequirement}</li>
                )}
                {errors.hospitalTourOption && (
                  <li>{errors.hospitalTourOption}</li>
                )}
                {errors.breakfast && (
                  <li>{errors.breakfast}</li>
                )}
                {errors.lunch && (
                  <li>{errors.lunch}</li>
                )}
                {errors.dinner && (
                  <li>{errors.dinner}</li>
                )}
                {errors.schedule && (
                  <li>{errors.schedule}</li>
                )}
                {errors.training && (
                  <li>{errors.training}</li>
                )}
                {errors.assignment && (
                  <li>{errors.assignment}</li>
                )}
                {errors.facility && (
                  <li>{errors.facility}</li>
                )}
                {errors.doctors && (
                  <li>指導医情報に不備があります</li>
                )}
                {errors.hospitalTourText && (
                  <li>{errors.hospitalTourText}</li>
                )}
              </ul>
            </div>
          )}

          {/* 必須項目の説明 */}
          <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 mb-6 rounded-md">
            <p className="flex items-center mb-2">
              <span className="mr-2">
                <Info className="h-5 w-5" />
              </span>
              <span className="font-medium">必須項目について</span>
            </p>
            <p className="text-sm ml-7">
              <span className="text-red-500 mr-1">*</span>
              マークの付いた項目は入力必須です。全ての必須項目を入力してください。
            </p>
          </div>

          {/* ターム選択 */}
          {isCopyDialogOpen && (
            <div className="hospital-report-copy-modal-overlay">
              <div className="hospital-report-copy-modal">
                <div className="hospital-report-copy-modal-header">
                  <div>
                    <h3 className="hospital-report-copy-modal-title">
                      既存のレポート内容をコピーする
                    </h3>
                    <p className="hospital-report-copy-modal-description">
                      同じ病院×科のレポートを、コホートやタームをまたいで選べます。
                    </p>
                  </div>
                  <button
                    type="button"
                    className="hospital-report-copy-modal-close"
                    onClick={() => {
                      setIsCopyDialogOpen(false);
                      setSelectedCopyReportId(null);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {copyCandidatesError && (
                  <div className="hospital-report-ai-error">{copyCandidatesError}</div>
                )}

                <div className="hospital-report-copy-list">
                  {copyCandidates.map((candidate) => (
                    <button
                      key={candidate.id}
                      type="button"
                      className="hospital-report-copy-card"
                      onClick={() => handleCopyFromExistingReport(candidate.id)}
                      disabled={selectedCopyReportId === candidate.id}
                    >
                      <div className="hospital-report-copy-card-top">
                        <span className="hospital-report-copy-card-cohort">
                          {candidate.cohort_name || `コホート ${candidate.cohort_id}`}
                        </span>
                        <span className="hospital-report-copy-card-term">
                          第{candidate.term}ターム
                        </span>
                      </div>
                      <div className="hospital-report-copy-card-body">
                        <div className="hospital-report-copy-card-name">
                          {candidate.hospital_name} / {candidate.department_name}
                        </div>
                        <div className="hospital-report-copy-card-updated">
                          更新: {candidate.updated_at ? new Date(candidate.updated_at).toLocaleString('ja-JP') : '-'}
                        </div>
                      </div>
                      <div className="hospital-report-copy-card-action">
                        {selectedCopyReportId === candidate.id ? (
                          <>
                            <Loader2 className="animate-spin h-4 w-4" />
                            読み込み中...
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4" />
                            この内容をコピー
                          </>
                        )}
                      </div>
                    </button>
                  ))}
                </div>

                <div className="hospital-report-copy-modal-footer">
                  <button
                    type="button"
                    className="hospital-report-ai-secondary"
                    onClick={() => {
                      setIsCopyDialogOpen(false);
                      setSelectedCopyReportId(null);
                    }}
                  >
                    キャンセル
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="mb-8">
            <h3 className="mt-4 mb-4 text-2xl font-semibold text-blue-800">
              ターム選択
            </h3>
            <div>
              <label htmlFor="termSelection" className="block mb-1 text-sm font-medium text-gray-600">
                タームを選択してください (1〜7)
                <span className="text-red-500 text-xs align-top">*</span>
              </label>
              <div className="flex items-center">
                <select
                  id="termSelection"
                  className={`w-full sm:w-1/2 h-10 border ${errors.selectedTerm ? 'border-red-500' : 'border-gray-300'} rounded px-3 py-2 bg-white text-base focus:outline-none focus:ring focus:ring-blue-500`}
                  value={selectedTerm}
                  // ★ ターム選択は isDirty の対象外とする（データロードのトリガーのため）
                  onChange={(e) => setSelectedTerm(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="">-- 選択 --</option>
                  {[1, 2, 3, 4, 5, 6, 7].map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
              {errors.selectedTerm && (
                <p className="text-red-500 text-sm mt-1">{errors.selectedTerm}</p>
              )}
            </div>
          </div>

          <div className="mb-8">
            <h3 className="mt-4 mb-4 text-2xl font-semibold text-blue-800">
              コホート選択
            </h3>
            <div className="flex flex-wrap gap-4 items-end">
              <div className="w-full sm:w-1/2">
                <label htmlFor="cohortSelection" className="block mb-1 text-sm font-medium text-gray-600">
                  保存先コホート
                  <span className="text-red-500 text-xs align-top">*</span>
                </label>
                {isAdminMode ? (
                  <select
                    id="cohortSelection"
                    className={`w-full h-10 border ${errors.selectedCohort ? 'border-red-500' : 'border-gray-300'} rounded px-3 py-2 bg-white text-base focus:outline-none focus:ring focus:ring-blue-500`}
                    value={selectedCohortId}
                    onChange={(e) => setSelectedCohortId(e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="">-- コホートを選択 --</option>
                    {cohortOptions.map((cohort) => (
                      <option key={cohort.id} value={cohort.id}>
                        {cohort.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="cohortSelection"
                    className={`w-full h-10 border ${errors.selectedCohort ? 'border-red-500' : 'border-gray-300'} rounded px-3 py-2 bg-gray-50 text-base`}
                    value={selectedCohort?.name || userInfo?.cohort_name || ''}
                    readOnly
                    disabled
                  />
                )}
                {errors.selectedCohort && (
                  <p className="text-red-500 text-sm mt-1">{errors.selectedCohort}</p>
                )}
              </div>
              <div className="w-full sm:w-auto">
                <button
                  type="button"
                  onClick={handleOpenCopyDialog}
                  className="hospital-report-copy-trigger px-4 py-2 border border-blue-300 text-blue-700 rounded-lg font-medium hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  disabled={
                    isLoading ||
                    isCopyingPrevious ||
                    !selectedTerm ||
                    !selectedCohortId ||
                    !(currentAssignment?.department_term_capacity_id || selectedCapacity?.id)
                  }
                >
                  {isCopyingPrevious ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4" />
                      コピー中...
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      <span className="hospital-report-copy-trigger-text">
                        既存のレポート内容をコピーする
                      </span>
                      前コホートをコピー
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="mt-4 mb-4 text-2xl font-semibold text-blue-800">
              配属病院・部門選択
            </h3>
            {isAdminMode ? (
              <div className="flex flex-wrap gap-4">
                <div className="w-full sm:w-1/2">
                  <label htmlFor="hospitalSelect" className="block mb-1 text-sm font-medium text-gray-600">
                    病院名
                  </label>
                  <select
                    id="hospitalSelect"
                    className={`w-full h-10 border ${errors.selectedHospital ? 'border-red-500' : 'border-gray-300'} rounded px-3 py-2 bg-white text-base focus:outline-none focus:ring focus:ring-blue-500`}
                    value={selectedHospitalId}
                    onChange={(e) => setSelectedHospitalId(e.target.value)}
                    disabled={isLoading || !selectedTerm}
                  >
                    <option value="">-- 病院を選択 --</option>
                    {adminHospitalOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                  {errors.selectedHospital && (
                    <p className="text-red-500 text-sm mt-1">{errors.selectedHospital}</p>
                  )}
                </div>
                <div className="w-full sm:w-1/2">
                  <label htmlFor="departmentSelect" className="block mb-1 text-sm font-medium text-gray-600">
                    部門名
                  </label>
                  <select
                    id="departmentSelect"
                    className={`w-full h-10 border ${errors.selectedDepartment ? 'border-red-500' : 'border-gray-300'} rounded px-3 py-2 bg-white text-base focus:outline-none focus:ring focus:ring-blue-500`}
                    value={selectedDepartmentId}
                    onChange={(e) => setSelectedDepartmentId(e.target.value)}
                    disabled={isLoading || !selectedHospitalId}
                  >
                    <option value="">-- 部門を選択 --</option>
                    {adminDepartmentOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                  {errors.selectedDepartment && (
                    <p className="text-red-500 text-sm mt-1">{errors.selectedDepartment}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-4">
                <div className="w-full sm:w-1/2">
                  <label htmlFor="hospitalName" className="block mb-1 text-sm font-medium text-gray-600">
                    病院名
                  </label>
                  <input
                    id="hospitalName"
                    className="w-full h-10 border border-gray-300 rounded px-3 py-2 bg-white text-base focus:outline-none"
                    value={autoHospitalName}
                    placeholder="病院名を表示"
                    disabled
                  />
                </div>
                <div className="w-full sm:w-1/2">
                  <label htmlFor="departmentName" className="block mb-1 text-sm font-medium text-gray-600">
                    部門名
                  </label>
                  <input
                    id="departmentName"
                    className="w-full h-10 border border-gray-300 rounded px-3 py-2 bg-white text-base focus:outline-none"
                    value={autoDepartmentName}
                    placeholder="部門名を表示"
                    disabled
                  />
                </div>
              </div>
            )}
            <hr className="mt-4 border-gray-300" />
          </div>

          {/* スケジュール */}
          <div className="mb-8">
            <h3 className="mt-4 mb-4 text-2xl font-semibold text-blue-800">
              スケジュール
              <span className="text-red-500 text-xs align-top">*</span>
            </h3>
            <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 mb-4 rounded-md">
              <p className="text-sm">
                <span className="font-medium">注意事項：</span>
                <ul className="list-disc pl-5 mt-2">
                  <li>実習開始時間と終了時間を必ず含めてください</li>
                  <li>曜日ごとスケジュールを記載してください</li>
                </ul>
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-200 text-gray-800 p-4 mb-4 rounded-md">
              <p className="text-sm font-medium mb-2">テキストエディタの使い方：</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                <div className="flex items-center">
                  <span className="font-bold mr-1">B</span>
                  <span className="text-xs">太字</span>
                </div>
                <div className="flex items-center">
                  <span className="italic mr-1">I</span>
                  <span className="text-xs">斜体</span>
                </div>
                <div className="flex items-center">
                  <span className="underline mr-1">U</span>
                  <span className="text-xs">下線</span>
                </div>
                <div className="flex items-center">
                  <span className="line-through mr-1">S</span>
                  <span className="text-xs">打ち消し線</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">"</span>
                  <span className="text-xs">引用</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">&lt;&gt;</span>
                  <span className="text-xs">コード</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">🔗</span>
                  <span className="text-xs">リンク</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">🖼️</span>
                  <span className="text-xs">画像</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">🎞️</span>
                  <span className="text-xs">動画</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">ƒₓ</span>
                  <span className="text-xs">数式</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">H1/H2</span>
                  <span className="text-xs">見出し</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">1.</span>
                  <span className="text-xs">番号付きリスト</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">•</span>
                  <span className="text-xs">箇条書き</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">←</span>
                  <span className="text-xs">インデント減</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">→</span>
                  <span className="text-xs">インデント増</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">x₂</span>
                  <span className="text-xs">下付き</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">x²</span>
                  <span className="text-xs">上付き</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">←</span>
                  <span className="text-xs">左揃え</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">↔</span>
                  <span className="text-xs">中央揃え</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">→</span>
                  <span className="text-xs">右揃え</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">⇔</span>
                  <span className="text-xs">両端揃え</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">¶</span>
                  <span className="text-xs">改行記号</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">N</span>
                  <span className="text-xs">スタイル</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">A</span>
                  <span className="text-xs">テキスト色</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">■</span>
                  <span className="text-xs">背景色</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">Aa</span>
                  <span className="text-xs">フォント</span>
                </div>
              </div>
            </div>
            {/* ★ ステップ2: JSX の修正 */}
            <RichTextEditor
              value={schedule}
              onChange={handleScheduleChange}
              placeholder="スケジュールを記載してください。実習開始時間と終了時間を必ず含めてください。"
              disabled={isLoading}
            />
            <hr className="mt-4 border-gray-300" />
          </div>

          {/* 実習内容 */}
          <div className="mb-8">
            <h3 className="mt-4 mb-4 text-2xl font-semibold text-blue-800">
              実習内容
              <span className="text-red-500 text-xs align-top">*</span>
            </h3>
            <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 mb-4 rounded-md">
              <p className="text-sm">
                <span className="font-medium">注意事項：</span>
                <ul className="list-disc pl-5 mt-2">
                  <li>具体的な実習内容（手術・外来など）を記載してください</li>
                  <li>手術・外来の内容と所要時間など詳細を記載してください</li>
                  <li>実習内での質問事項もここに記載してください</li>
                </ul>
              </p>
            </div>
            {/* ★ ステップ2: JSX の修正 */}
            <RichTextEditor
              value={training}
              onChange={handleTrainingChange}
              placeholder="実習内容を記載してください"
              disabled={isLoading}
            />
            <hr className="mt-4 border-gray-300" />
          </div>

          {/* 課題 */}
          <div className="mb-8">
            <h3 className="form-section-label">
              課題
              <span className="text-red-500 text-xs align-top">*</span>
            </h3>
            <div className="assignment-button-group">
              {/* ★ ステップ2: JSX の修正 */}
              <button
                type="button"
                className={`assignment-button ${hasAssignment ? 'assignment-button-active' : ''}`}
                onClick={() => handleHasAssignmentChange(true)}
                disabled={isLoading}
              >
                課題あり
              </button>
              <button
                type="button"
                className={`assignment-button ${!hasAssignment ? 'assignment-button-active' : ''}`}
                onClick={() => handleHasAssignmentChange(false)}
                disabled={isLoading}
              >
                課題なし
              </button>
            </div>
            {hasAssignment && (
              // ★ ステップ2: JSX の修正
              <RichTextEditor
                value={assignment}
                onChange={handleAssignmentChange}
                placeholder="課題の内容を記入してください"
                disabled={isLoading}
              />
            )}
            <hr className="mt-4 border-gray-300" />
          </div>

          {/* 指導医情報 */}
          <div className="mb-8">
            <h3 className="mt-4 mb-4 text-2xl font-semibold text-blue-800">
              指導医
              <span className="text-red-500 text-xs align-top">*</span>
            </h3>
            <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 mb-4 rounded-md">
              <p className="text-sm">
                <span className="font-medium">注意事項：</span>
                <ul className="list-disc pl-5 mt-2">
                  <li>関わったグループ全体の医師および指導医を記載してください</li>
                </ul>
              </p>
            </div>
            {doctors.map((doctor, idx) => (
              <div key={doctor.id} className="mb-4 p-4 border border-blue-200 rounded bg-white">
                <div className="mb-2 flex justify-between items-center">
                  <label className="block text-sm font-medium text-gray-600">
                    医師情報 {idx + 1} {/* ラベルを少し明確に */}
                  </label>
                  {/* ★ removeDoctorBox は既に isDirty を更新 */}
                  <button
                    type="button"
                    onClick={() => removeDoctorBox(idx)}
                    className="text-red-500 hover:text-red-700 focus:outline-none"
                    aria-label="医師情報を削除"
                    disabled={isLoading || doctors.length <= 1} // 最後の1つは削除不可
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-medium mb-1" htmlFor={`doctorName-${doctor.id}`}>
                    医師名
                    <span className="text-red-500 text-xs align-top">*</span>
                  </label>
                  {/* ★ handleDoctorChange は既に isDirty を更新 */}
                  <input
                    type="text"
                    id={`doctorName-${doctor.id}`}
                    value={doctor.doctorName}
                    onChange={(e) => handleDoctorChange(idx, 'doctorName', e.target.value)}
                    className={`w-full p-2 border ${errors.doctors && errors.doctors[idx]?.doctorName ? 'border-red-500' : 'border-gray-300'} rounded doctor-input`}
                    placeholder="医師名"
                    disabled={isLoading}
                  />
                  {errors.doctors && errors.doctors[idx]?.doctorName && (
                    <p className="text-red-500 text-sm mt-1">{errors.doctors[idx].doctorName}</p>
                  )}
                </div>
                <div className="mb-2">
                  <label className="block text-gray-700 text-sm font-medium mb-1" htmlFor={`doctorNote-${doctor.id}`}>
                    内容
                    <span className="text-red-500 text-xs align-top">*</span>
                  </label>
                  {/* ★ handleDoctorChange は既に isDirty を更新 */}
                  <DoctorNoteEditor
                    value={doctor.doctorNote}
                    onChange={(value) => handleDoctorChange(idx, 'doctorNote', value)}
                    placeholder="コメントを入力"
                    disabled={isLoading}
                  />
                  {errors.doctors && errors.doctors[idx]?.doctorNote && (
                    <p className="text-red-500 text-sm mt-1">{errors.doctors[idx].doctorNote}</p>
                  )}
                </div>
              </div>
            ))}
            {/* ★ addDoctorBox は既に isDirty を更新 */}
            <button
              type="button"
              onClick={addDoctorBox}
              className="inline-flex items-center mt-2 px-4 py-2 border border-blue-600 rounded bg-white text-blue-600 font-semibold transition duration-200 hover:bg-blue-50"
              disabled={isLoading}
            >
              <Plus className="mr-2 h-5 w-5" />
              <span className="text-sm">医師記載欄を追加</span>
            </button>
            <hr className="mt-4 border-gray-300" />
          </div>

          {/* 病院見学 */}
          <div className="mb-8">
            <h3 className="mt-4 mb-4 text-2xl font-semibold text-blue-800">
              病院見学
              <span className="text-red-500 text-xs align-top">*</span>
            </h3>
            <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 mb-4 rounded-md">
              <p className="text-sm">
                <span className="font-medium">注意事項：</span>
                <ul className="list-disc pl-5 mt-2">
                  <li>可能であった場合は、連絡方法と日数を記入してください</li>
                  <li>断られた場合は「不可」を選択してください</li>
                  <li>聞いていない場合は「わからない」を選択してください</li>
                </ul>
              </p>
            </div>
            <div className="flex gap-4 mb-4">
              <label className="inline-flex items-center">
                {/* ★ ステップ2: JSX の修正 */}
                <input
                  type="radio"
                  name="hospitalTour"
                  value="possible"
                  checked={hospitalTourOption === 'possible'}
                  onChange={handleHospitalTourOptionChange}
                  className={`form-radio ${errors.hospitalTourOption ? 'text-red-500 border-red-500' : 'text-blue-600'}`}
                  disabled={isLoading}
                />
                <span className="ml-1">可</span>
              </label>
              <label className="inline-flex items-center">
                {/* ★ ステップ2: JSX の修正 */}
                <input
                  type="radio"
                  name="hospitalTour"
                  value="impossible"
                  checked={hospitalTourOption === 'impossible'}
                  onChange={handleHospitalTourOptionChange}
                  className={`form-radio ${errors.hospitalTourOption ? 'text-red-500 border-red-500' : 'text-blue-600'}`}
                  disabled={isLoading}
                />
                <span className="ml-1">不可</span>
              </label>
              <label className="inline-flex items-center">
                {/* ★ ステップ2: JSX の修正 */}
                <input
                  type="radio"
                  name="hospitalTour"
                  value="unknown"
                  checked={hospitalTourOption === 'unknown'}
                  onChange={handleHospitalTourOptionChange}
                  className={`form-radio ${errors.hospitalTourOption ? 'text-red-500 border-red-500' : 'text-blue-600'}`}
                  disabled={isLoading}
                />
                <span className="ml-1">わからない</span>
              </label>
              {errors.hospitalTourOption && (
                <p className="text-red-500 text-sm ml-4">{errors.hospitalTourOption}</p>
              )}
            </div>
            <div className="mb-4">
              {/* ★ ステップ2: JSX の修正 */}
              <RichTextEditor
                value={hospitalTourText}
                onChange={handleHospitalTourTextChange}
                placeholder="見学に関する情報を入力してください"
                disabled={isLoading}
              />
              {errors.hospitalTourText && (
                <p className="text-red-500 text-sm mt-1">{errors.hospitalTourText}</p>
              )}
            </div>
            <hr className="mt-4 border-gray-300" />
          </div>

          {/* 設備・宿泊 */}
          <div className="mb-8">
            <h3 className="mt-4 mb-4 text-2xl font-semibold text-blue-800">
              設備・宿泊
              <span className="text-red-500 text-xs align-top">*</span>
            </h3>
            <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 mb-4 rounded-md">
              <p className="text-sm">
                <span className="font-medium">注意事項：</span>
                <ul className="list-disc pl-5 mt-2">
                  <li>大学病院の場合は「なし」と入力してください</li>
                </ul>
              </p>
            </div>
            {/* ★ ステップ2: JSX の修正 */}
            <RichTextEditor
              value={facility}
              onChange={handleFacilityChange}
              placeholder="設備や宿泊に関する情報を記載してください"
              disabled={isLoading}
            />
            <hr className="mt-4 border-gray-300" />
          </div>

          {/* その他 */}
          <div className="mb-8">
            <h3 className="mt-4 mb-4 text-2xl font-semibold text-blue-800">
              その他
            </h3>
            {/* ★ ステップ2: JSX の修正 */}
            <RichTextEditor
              value={other}
              onChange={handleOtherChange}
              placeholder="その他の情報を記入してください"
              disabled={isLoading}
            />
            <hr className="mt-4 border-gray-300" />
          </div>

          {/* 全体評価 */}
          <div className="mb-8">
            <h3 className="mt-4 mb-4 text-2xl font-semibold text-blue-800">
              全体評価
            </h3>
            <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 mb-4 rounded-md">
              <p className="text-sm">
                <span className="font-medium">注意事項：</span>
                <ul className="list-disc pl-5 mt-2">
                  <li>忙しさは下の根拠入力から自動判定されます</li>
                  <li>教育・雰囲気・生活面は、自由記載の内容と一致するように入力してください</li>
                </ul>
              </p>
            </div>
            <div className="evaluation-container">
              <div className="hospital-report-evaluation-section">
                <div className="hospital-report-evaluation-section-title">
                  忙しさの根拠入力
                  <span className="text-red-500 text-xs align-top">*</span>
                </div>
                <div className="hospital-report-busyness-summary">
                  <span className="hospital-report-busyness-summary-label">判定結果</span>
                  <span className="hospital-report-busyness-summary-value">
                    {effectiveBusyness
                      ? `${effectiveBusyness} / 5 (${BUSYNESS_LABELS[effectiveBusyness] || ''})`
                      : '未判定'}
                  </span>
                </div>
                <div className="hospital-report-busyness-grid">
                  <label className="hospital-report-busyness-field">
                    <span>始業時刻</span>
                    <input
                      type="time"
                      value={typicalStartTime}
                      onChange={handleBusyInputChange(setTypicalStartTime)}
                      disabled={isLoading}
                      className="form-input"
                    />
                    {errors.typicalStartTime && <span className="text-red-500 text-sm">{errors.typicalStartTime}</span>}
                  </label>
                  <label className="hospital-report-busyness-field">
                    <span>終業時刻</span>
                    <input
                      type="time"
                      value={typicalEndTime}
                      onChange={handleBusyInputChange(setTypicalEndTime)}
                      disabled={isLoading}
                      className="form-input"
                    />
                    {errors.typicalEndTime && <span className="text-red-500 text-sm">{errors.typicalEndTime}</span>}
                  </label>
                  <label className="hospital-report-busyness-field">
                    <span>17時以降になる日数</span>
                    <select
                      value={lateDaysPerWeek}
                      onChange={handleBusyInputChange(setLateDaysPerWeek)}
                      disabled={isLoading}
                      className="form-select"
                    >
                      <option value="">選択</option>
                      {[0, 1, 2, 3, 4, 5, 6, 7].map((value) => (
                        <option key={value} value={value}>{value}日/週</option>
                      ))}
                    </select>
                    {errors.lateDaysPerWeek && <span className="text-red-500 text-sm">{errors.lateDaysPerWeek}</span>}
                  </label>
                  <label className="hospital-report-busyness-field">
                    <span>自習時間</span>
                    <select
                      value={freeStudyTime}
                      onChange={handleBusyInputChange(setFreeStudyTime)}
                      disabled={isLoading}
                      className="form-select"
                    >
                      <option value="">選択</option>
                      {FREE_STUDY_TIME_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    {errors.freeStudyTime && <span className="text-red-500 text-sm">{errors.freeStudyTime}</span>}
                  </label>
                </div>
                <div className="evaluation-row hospital-report-busyness-night-duty">
                  <div className="evaluation-label">
                    当直・夜遅い対応
                    <span className="text-red-500 text-xs align-top">*</span>
                  </div>
                  <div className="evaluation-radio-group">
                    <span className="evaluation-left-label"></span>
                    <div className="evaluation-radio-buttons">
                      <label className="evaluation-radio">
                        <input
                          type="radio"
                          name="hasNightDuty"
                          value="yes"
                          checked={hasNightDuty === true}
                          className="form-radio text-blue-600"
                          onChange={handleBusyBooleanChange(setHasNightDuty)}
                          disabled={isLoading}
                        />
                        <span className="ml-1 text-sm">あり</span>
                      </label>
                      <label className="evaluation-radio">
                        <input
                          type="radio"
                          name="hasNightDuty"
                          value="no"
                          checked={hasNightDuty === false}
                          className="form-radio text-blue-600"
                          onChange={handleBusyBooleanChange(setHasNightDuty)}
                          disabled={isLoading}
                        />
                        <span className="ml-1 text-sm">なし</span>
                      </label>
                    </div>
                    <span className="evaluation-right-label"></span>
                  </div>
                </div>
                {errors.hasNightDuty && <p className="text-red-500 text-sm">{errors.hasNightDuty}</p>}
                {errors.busyness && <p className="text-red-500 text-sm mt-2">{errors.busyness}</p>}
              </div>

              {EVALUATION_FIELD_GROUPS.map((group) => (
                <div className="hospital-report-evaluation-section" key={group.section}>
                  <div className="hospital-report-evaluation-section-title">{group.section}</div>
                  {group.items.map((item) => {
                    const setter = getEvaluationFieldSetter(item.key);
                    const value = getEvaluationFieldValue(item.key);
                    return (
                      <div className="evaluation-row" key={item.key}>
                        <div className="evaluation-label">
                          {item.label}
                          <span className="text-red-500 text-xs align-top">*</span>
                        </div>
                        <div className="evaluation-radio-group">
                          <span className="evaluation-left-label">{item.left}</span>
                          <div className="evaluation-radio-buttons">
                            {[1, 2, 3, 4, 5].map((n) => (
                              <label key={n} className="evaluation-radio">
                                <input
                                  type="radio"
                                  name={item.key}
                                  value={n}
                                  checked={value === n}
                                  className="form-radio text-blue-600"
                                  onChange={(e) => handleRadioChange(e, setter)}
                                  disabled={isLoading}
                                />
                              </label>
                            ))}
                          </div>
                          <span className="evaluation-right-label">{item.right}</span>
                        </div>
                        {errors[item.key] && <p className="text-red-500 text-sm mt-1">{errors[item.key]}</p>}
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* 食事提供（1行に統合） */}
              <div className="hospital-report-evaluation-section">
                <div className="hospital-report-evaluation-section-title">
                  食事提供
                  <span className="text-red-500 text-xs align-top">*</span>
                </div>
                <div className="evaluation-select-group meals-container">
                  <div className="meal-item">
                    <span className="meal-label">朝食</span>
                    <div className="evaluation-select">
                      <select
                        id="breakfast"
                        className="form-select"
                        value={breakfast}
                        onChange={handleBreakfastChange}
                        disabled={isLoading}
                      >
                        <option value="">選択</option>
                        <option value="none">なし</option>
                        <option value="free">無料</option>
                        <option value="paid">有料</option>
                      </select>
                    </div>
                  </div>
                  <div className="meal-item">
                    <span className="meal-label">昼食</span>
                    <div className="evaluation-select">
                      {/* ★ ステップ2: JSX の修正 */}
                      <select
                        id="lunch"
                        className="form-select"
                        value={lunch}
                        onChange={handleLunchChange}
                        disabled={isLoading}
                      >
                        <option value="">選択</option>
                        <option value="none">なし</option>
                        <option value="free">無料</option>
                        <option value="paid">有料</option>
                      </select>
                    </div>
                  </div>
                  <div className="meal-item">
                    <span className="meal-label">夕食</span>
                    <div className="evaluation-select">
                      {/* ★ ステップ2: JSX の修正 */}
                      <select
                        id="dinner"
                        className="form-select"
                        value={dinner}
                        onChange={handleDinnerChange}
                        disabled={isLoading}
                      >
                        <option value="">選択</option>
                        <option value="none">なし</option>
                        <option value="free">無料</option>
                        <option value="paid">有料</option>
                      </select>
                    </div>
                  </div>
                </div>
                {(errors.breakfast || errors.lunch || errors.dinner) && (
                  <p className="text-red-500 text-sm mt-2">
                    {errors.breakfast || errors.lunch || errors.dinner}
                  </p>
                )}
              </div>
            </div>
            <hr className="mt-4 border-gray-300" />
          </div>

          {/* 送信ボタン群 */}
          <div className="flex flex-wrap justify-end gap-4 mt-12">
            {/* ★ handleTempSave は既に isDirty を更新 */}
            <button
              type="submit"
              className="px-8 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold transition duration-200 hover:bg-gray-300 flex items-center justify-center"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-800" />
                  処理中...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-5 w-5" />
                  一時保存
                </>
              )}
            </button>
            {/* ★ handlePDFOutput は既に isDirty を更新 */}
            <button
              type="button"
              onClick={handlePDFOutput}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold transition duration-200 hover:bg-blue-700 flex items-center justify-center"
              disabled={isLoading}
            >
              {/* ★ isLoading でボタン表示を切り替え */}
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" />
                  処理中...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-5 w-5" />
                  登録
                </>
              )}
            </button>
          </div>
        </form>

        {/* フローティングアクションボタン */}
        <div className="fixed-action-buttons">
          {/* ★ handleTempSave は既に isDirty を更新 */}
          <button
            type="button"
            className="action-button action-button-save"
            onClick={handleTempSave}
            disabled={isLoading}
            title="一時保存"
          >
            一時
            <br />
            保存
          </button>
          {/* ★ handlePDFOutput は既に isDirty を更新 */}
          <button
            type="button"
            className="action-button action-button-pdf"
            onClick={handlePDFOutput}
            disabled={isLoading}
            title="登録"
          >
            登録
          </button>
        </div>
      </div>
    </div>
  );
}

export default HospitalReportForm;
